import { AuthorizationLevel } from 'app/API.service.custom';
import { Type } from '@angular/core';
import { DialogType } from '../../shared/dialog/dialog.service';
import { DialogConfig } from '../../shared/dialog/dialog-config';
import { AdHocReminderComponent } from '../../shared/help/ad-hoc-reminder/ad-hoc-reminder.component';
import { SupportComponent } from '../../shared/support/support/support.component';
import { AddContactComponent } from '../../shared/add-contact/add-contact.component';
import { AddCompanyComponent } from '../../shared/add-company/add-company.component';
import { LogCallComponent } from '../../shared/log-call/log-call.component';
import { LogMeetingComponent } from '../../shared/log-meeting/log-meeting.component';
import { LogUpdateComponent } from '../../shared/log-update/log-update.component';
import { UploadComponent } from '../../shared/upload/upload.component';
import { AdminActionsComponent } from '../../shared/help/admin-actions/admin-actions.component';
import { BulkUploadStepsComponent } from '../../shared/contacts/bulk-upload-steps/bulk-upload-steps.component';
import { DefineDistributionComponent } from '../../shared/define-distribution/define-distribution.component';
import { AddEmployeeBulkComponent } from '../../shared/employee/add-employee-bulk/add-employee-bulk.component';
import { CreateTemplateComponent } from '../../shared/templates/create-template/create-template.component';
import { CreateFormComponent } from '../../shared/account/create-form/create-form.component';
import { CreateTeamAccountComponent } from '../../shared/account/create-team-account/create-team-account.component';
import { InviteToGroupNewComponent } from '../../shared/employee/invite-to-group-new/invite-to-group-new.component';
import { CreatePropertyComponent } from '../../shared/properties/create-property/create-property.component';
import { UpdateAccountComponent } from '../../shared/account/update-account/update-account.component';
import { UpdateAccountDefaultsComponent } from '../../shared/account/update-account-defaults/update-account-defaults.component';
import { QuotationDefaultsComponent } from '../../shared/product/quotation-defaults/quotation-defaults.component';
import { CreateTasksComponent } from '../../shared/create-tasks/create-tasks.component';
import { SearchComponent } from '../../shared/search/search.component';
import { TeamInboxComponent } from '../../shared/conversation/team-inbox/team-inbox.component';
import { SharedInboxComponent } from '../../shared/conversation/shared-inbox/shared-inbox.component';
import { CreateWabaCampaignComponent } from '../../shared/campaign/create-waba-campaign/create-waba-campaign.component';
import { AddProductComponent } from '../../shared/product/add-product/add-product.component';
import { ChartGalleryComponent } from '../../shared/report/chart-gallery/chart-gallery.component';
import { VideoCallsComponent } from '../../shared/callings/video-calls/video-calls.component';
import { CreateTicketCategoryComponent } from '../../shared/account/create-ticket-category/create-ticket-category.component';
import { CreateTicketStageComponent } from '../../shared/account/create-ticket-stage/create-ticket-stage.component';
import { QueryBuilderComponent } from '../../shared/query-builder/query-builder/query-builder.component';
import { ManuallySelectContactsComponent } from '../../shared/callings/manually-select-contacts/manually-select-contacts.component';
import { CreateLeadTagComponent } from '../../shared/account/create-lead-tag/create-lead-tag.component';
import { CreateLeadSourceComponent } from '../../shared/account/create-lead-source/create-lead-source.component';
import { CreateDocumentCategoryComponent } from '../../shared/account/create-document-category/create-document-category.component';
import { CreateVisitOutcomeComponent } from '../../shared/account/create-visit-outcome/create-visit-outcome.component';
import { CreateTaskTypeComponent } from '../../shared/account/create-task-type/create-task-type.component';
import { CreateDealStageComponent } from '../../shared/account/create-deal-stage/create-deal-stage.component';
import { CreateCallOutcomeComponent } from '../../shared/account/create-call-outcome/create-call-outcome.component';
import { CreateGroupComponent } from '../../shared/employee/create-group/create-group.component';

type DiscoverableRole = 'member' | 'manager' | 'admin' | 'superadmin';

export interface ActionDiscoverabilityConfig {
  route: string;
  id?: string;
  title?: string;
  description?: string;
  icon?: string;
  priority?: number;
  showOnce?: boolean;
  requiredRoles?: DiscoverableRole[];
  workflowId?: string;
  videoUrl?: string;
  actionId?: string;
  targetSelector?: string;
}

export type FeatureCategory =
  | 'Lead & Contact Management'
  | 'Communication & Calling'
  | 'Messaging & WhatsApp'
  | 'Deals & Quotations'
  | 'Tasks & Follow-ups'
  | 'Reports & Analytics'
  | 'Team & Users'
  | 'Settings & Customization'
  | 'Campaigns & Automation'
  | 'Help & Tutorials';

export type ActionPlatform = 'web' | 'app';

// Platform-specific content for steps and videos
export interface PlatformContent {
  steps?: string[];           // Step-by-step instructions for this platform
  videoUrl?: string;          // Tutorial video URL for this platform
}

// Onboarding configuration for trial/setup phase
export type OnboardingTier = 'critical' | 'important' | 'helpful';

export interface OnboardingConfig {
  tier: OnboardingTier;                 // Priority tier
  order: number;                        // Order within tier (1, 2, 3...)
  title?: string;                       // Override text for onboarding display
  description?: string;                 // Short description for checklist
  completionKey?: string;               // Key path to check completion (e.g., 'account.totalLeads')
  completionCheck?: 'boolean' | 'count' | 'exists';  // How to verify completion
  completionThreshold?: number;         // For 'count' type (default: 1)
  estimatedMinutes?: number;            // "~2 min"
  ctaLabel?: string;                    // Custom CTA button text (default: "Start")
  adminOnly?: boolean;                  // Only show to admins
  // Visibility control
  showInTrial?: boolean;                // Show in trial mode (unified-command-palette quick start)
  showInFullSetup?: boolean;            // Show in full setup (get-started-new comprehensive)
}

export interface AssistantActionDefinition {
  id: string;
  text: string;
  tooltip: string;
  icon: string;
  category: 'admin' | 'user' | 'common';
  featureCategory?: FeatureCategory;
  allowedRoles: AuthorizationLevel[];
  /** Account-scoped permission evaluated in addition to the broad role list. */
  requiredCapability?: 'catalogEdit';
  keywords?: string[];
  component?: Type<any>;
  route?: string;
  routeCommands?: any[];
  rootRoute?: string;
  subRoutes?: string[];
  dialogType?: DialogType;
  closeOnClickOutside?: boolean;
  dialogConfig?: DialogConfig;
  workflowId?: string; // References workflow JSON for guided multi-step actions
  requiresContext?: boolean; // True if action needs page context/selection
  steps?: string[]; // Text-based step-by-step instructions for contextual actions
  discoverable?: ActionDiscoverabilityConfig;
  onboardingRelevant?: boolean;
  onboarding?: OnboardingConfig;           // Onboarding checklist configuration
  videoUrl?: string;                        // Fallback shared video URL
  web?: PlatformContent;                    // Web-specific content
  app?: PlatformContent;                    // Mobile app-specific content
}

const ALL_ROLES = [
  AuthorizationLevel.MEMBER,
  AuthorizationLevel.MANAGER,
  AuthorizationLevel.ADMIN,
  AuthorizationLevel.SUPERADMIN
];

const MANAGER_PLUS = [
  AuthorizationLevel.MANAGER,
  AuthorizationLevel.ADMIN,
  AuthorizationLevel.SUPERADMIN
];

const ADMIN_ONLY = [
  AuthorizationLevel.ADMIN,
  AuthorizationLevel.SUPERADMIN
];

const SUPERADMIN_ONLY = [
  AuthorizationLevel.SUPERADMIN
];

/** Base CDN URL for all tutorial videos */
export const VIDEO_BASE_URL = 'https://du2rkllq8xmq3.cloudfront.net';

export const ASSISTANT_ACTION_DEFINITIONS: AssistantActionDefinition[] = [
  {
    id: 'add-reminder',
    text: 'Remind Me!!',
    tooltip: 'Ad Hoc Reminders',
    icon: 'circle_notifications',
    category: 'common',
    featureCategory: 'Tasks & Follow-ups',
    allowedRoles: ALL_ROLES,
    component: AdHocReminderComponent,
    rootRoute: '/home',
    closeOnClickOutside: false,
    videoUrl: 'videos/tasks/add-reminder.webm',
    web: {
      videoUrl: 'videos/tasks/add-reminder.webm'
    }
  },
  {
    id: 'add_single_lead',
    text: 'Add Single Lead',
    tooltip: 'Add a new lead quickly',
    icon: 'person',
    category: 'common',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    component: AddContactComponent,
    rootRoute: '/leads',
    closeOnClickOutside: false,
    workflowId: 'add-single-lead',
    keywords: ['lead', 'add', 'single', 'contact add', 'lead add karo', 'naya lead', 'new contact'],
    videoUrl: 'videos/leads/add-single-lead.webm',
    web: {
      videoUrl: 'videos/leads/add-single-lead.webm'
    },
    onboarding: {
      tier: 'critical',
      order: 1,
      title: 'Add your first lead',
      description: 'Create a contact to start tracking your sales pipeline',
      completionKey: 'account.totalLeads',
      completionCheck: 'count',
      completionThreshold: 1,
      estimatedMinutes: 1,
      showInTrial: true,
      showInFullSetup: true
    }
  },
  {
    id: 'log_call',
    text: 'Log Call',
    tooltip: 'Log a manual call',
    icon: 'phone',
    category: 'user',
    featureCategory: 'Communication & Calling',
    allowedRoles: ALL_ROLES,
    component: LogCallComponent,
    route: '/log-call',
    workflowId: 'log-call-outcome',
    keywords: ['call', 'log', 'activity', 'log call for lead', 'record call', 'call outcome', 'manual call log'],
    videoUrl: 'videos/calls/log-call.webm',
    web: {
      videoUrl: 'videos/calls/log-call.webm'
    },
    onboarding: {
      tier: 'critical',
      order: 3,
      title: 'Log your first call',
      description: 'Record a call activity to start tracking communication',
      completionKey: 'account.totalCalls',
      completionCheck: 'count',
      completionThreshold: 1,
      estimatedMinutes: 1,
      showInTrial: true,
      showInFullSetup: true
    }
  },
  {
    id: 'log_outcome',
    text: 'Log Call Outcome',
    tooltip: 'Record the outcome/disposition after a call',
    icon: 'assignment',
    category: 'user',
    featureCategory: 'Communication & Calling',
    allowedRoles: ALL_ROLES,
    component: LogCallComponent,
    route: '/log-call',
    workflowId: 'log-call-outcome',
    keywords: ['log outcome', 'call disposition', 'call result'],
    videoUrl: 'videos/calls/log-call.webm',
    web: {
      videoUrl: 'videos/calls/log-call.webm'
    }
  },
  {
    id: 'place_call',
    text: 'Place Call from Web',
    tooltip: 'Place a call to the selected lead using click-to-call or registered dialer',
    icon: 'call',
    category: 'user',
    featureCategory: 'Communication & Calling',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    keywords: ['place call', 'dial lead', 'call lead'],
    steps: [
      'Select lead in list or open detail panel',
      'Click the phone icon / "Call" button',
      'Choose SIM/Cloud dialer if prompted',
      'Call launches on registered device'
    ],
    videoUrl: 'videos/calls/place-call.webm',
    web: {
      videoUrl: 'videos/calls/place-call.webm'
    }
  },
  {
    id: 'auto_call',
    text: 'Configure Auto Call',
    tooltip: 'Enable auto-calling sequences for leads',
    icon: 'autorenew',
    category: 'admin',
    featureCategory: 'Campaigns & Automation',
    allowedRoles: MANAGER_PLUS,
    route: '/engagements/campaigns',
    keywords: ['auto call', 'auto dialing', 'call campaign'],
    steps: [
      'Go to Engagements > Campaigns',
      'Click "Create Call Campaign"',
      'Choose contact list, assign callers, set attempts',
      'Launch auto-call campaign'
    ]
  },
  {
    id: 'add_note',
    text: 'Add Note',
    tooltip: 'Log an update',
    icon: 'edit',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    component: LogUpdateComponent,
    route: '/add-note',
    videoUrl: 'videos/leads/add-note.webm',
    web: {
      videoUrl: 'videos/leads/add-note.webm'
    }
  },
  {
    id: 'create_task',
    text: 'Create Task',
    tooltip: 'Create a new task or reminder for yourself or assign to team members',
    icon: 'add_task',
    category: 'common',
    featureCategory: 'Tasks & Follow-ups',
    allowedRoles: ALL_ROLES,
    component: CreateTasksComponent,
    dialogType: DialogType.SIDE_MODAL,
    closeOnClickOutside: false,
    workflowId: 'create-task',
    keywords: ['task', 'reminder', 'todo', 'schedule', 'follow up'],
    videoUrl: 'videos/tasks/create-task.webm',
    web: {
      videoUrl: 'videos/tasks/create-task.webm'
    },
    onboarding: {
      tier: 'important',
      order: 1,
      title: 'Create a follow-up task',
      description: 'Set up a task or reminder to stay on top of your leads',
      completionKey: 'account.totalTasks',
      completionCheck: 'count',
      completionThreshold: 1,
      estimatedMinutes: 1,
      showInTrial: true,
      showInFullSetup: true
    }
  },
  {
    id: 'add_document',
    text: 'Add Document',
    tooltip: 'Upload supporting document',
    icon: 'cloud_upload',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    component: UploadComponent,
    route: '/leads/documents',
    videoUrl: 'videos/leads/add-document.webm',
    web: {
      videoUrl: 'videos/leads/add-document.webm'
    }
  },
  {
    id: 'global_search',
    text: 'Search',
    tooltip: 'Search for leads, deals, activities, or any content across the workspace',
    icon: 'search',
    category: 'common',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    component: SearchComponent,
    dialogType: DialogType.FULL_MODAL,
    closeOnClickOutside: true,
    keywords: ['search', 'find', 'lookup', 'query'],
    videoUrl: 'videos/leads/global-search.webm',
    web: {
      videoUrl: 'videos/leads/global-search.webm'
    },
    onboarding: {
      tier: 'important',
      order: 4,
      title: 'Try global search',
      description: 'Quickly find any lead, deal, or activity across your workspace',
      estimatedMinutes: 1,
      showInTrial: true,
      showInFullSetup: true
    }
  },
  {
    id: 'open_team_inbox',
    text: 'Team Inbox',
    tooltip: 'Open internal team messaging and collaboration inbox',
    icon: 'forum',
    category: 'common',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: ALL_ROLES,
    component: TeamInboxComponent,
    dialogType: DialogType.FULL_MODAL,
    closeOnClickOutside: false,
    videoUrl: 'videos/conversations/open-team-inbox.webm',
    web: {
      videoUrl: 'videos/conversations/open-team-inbox.webm'
    },
    keywords: ['team chat', 'internal messages', 'team inbox', 'collaboration', 'group chat', 'direct message', 'colleague chat', 'team se baat', 'internal chat', 'support chat']
  },
  {
    id: 'open_shared_inbox',
    text: 'Customer Inbox',
    tooltip: 'Open shared WhatsApp inbox for customer conversations',
    icon: 'inbox',
    category: 'common',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: ALL_ROLES,
    component: SharedInboxComponent,
    dialogType: DialogType.FULL_MODAL,
    closeOnClickOutside: false,
    videoUrl: 'videos/conversations/open-shared-inbox.webm',
    web: {
      videoUrl: 'videos/conversations/open-shared-inbox.webm'
    },
    keywords: ['inbox', 'whatsapp', 'messages', 'chat', 'customer conversations', 'lead inbox', 'shared inbox', 'whatsapp cloud api', 'instagram dm', 'chat widget', 'external chat', 'customer se baat', 'lead chat']
  },
  {
    id: 'get_assistance',
    text: 'Get Assistance',
    tooltip: 'Contact Support',
    icon: 'help',
    category: 'user',
    featureCategory: 'Help & Tutorials',
    allowedRoles: ALL_ROLES,
    component: SupportComponent,
    dialogType: DialogType.FULL_MODAL,
    rootRoute: '/home',
    keywords: ['support', 'assistance', 'help']
  },
  {
    id: 'add_company',
    text: 'Add Business',
    tooltip: 'Add a company/business',
    icon: 'business',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    component: AddCompanyComponent,
    rootRoute: '/companies',
    videoUrl: 'videos/leads/add-company.webm',
    web: {
      videoUrl: 'videos/leads/add-company.webm'
    }
  },
  {
    id: 'log_meeting',
    text: 'Log Meeting',
    tooltip: 'Capture meeting outcome',
    icon: 'people',
    category: 'user',
    featureCategory: 'Communication & Calling',
    allowedRoles: ALL_ROLES,
    component: LogMeetingComponent,
    rootRoute: '/meetings',
    subRoutes: ['log-meeting'],
    videoUrl: 'videos/calls/log-meeting.webm',
    web: {
      videoUrl: 'videos/calls/log-meeting.webm'
    }
  },
  {
    id: 'upload_lead',
    text: 'Bulk Upload Leads',
    tooltip: 'Upload leads using Excel/CSV templates',
    icon: 'upload_file',
    category: 'admin',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ADMIN_ONLY,
    component: BulkUploadStepsComponent,
    dialogType: DialogType.FULL_MODAL,
    rootRoute: '/leads',
    keywords: ['bulk upload', 'import leads', 'excel upload', 'csv upload']
  },
  {
    id: 'define_distribution',
    text: 'Set Up Lead Distribution',
    tooltip: 'Create lead routing rules — choose your lead source and who gets them',
    icon: 'alt_route',
    category: 'admin',
    featureCategory: 'Campaigns & Automation',
    allowedRoles: SUPERADMIN_ONLY,
    component: DefineDistributionComponent,
    dialogType: DialogType.FULL_MODAL,
    rootRoute: '/leads',
    videoUrl: 'videos/leads/define-distribution.webm',
    web: {
      videoUrl: 'videos/leads/define-distribution.webm'
    },
    workflowId: 'leads-distribution-automation'
  },
  {
    id: 'invite_user',
    text: 'Invite Users',
    tooltip: 'Invite team members to workspace',
    icon: 'group_add',
    category: 'admin',
    featureCategory: 'Team & Users',
    allowedRoles: SUPERADMIN_ONLY,
    component: AddEmployeeBulkComponent,
    dialogType: DialogType.FULL_MODAL,
    rootRoute: '/admin/users',
    videoUrl: 'videos/admin/invite-user.webm',
    web: {
      videoUrl: 'videos/admin/invite-user.webm'
    },
    keywords: ['invite', 'user', 'employee', 'add user', 'add employee', 'team member', 'new user', 'user add karo', 'employee add', 'naya user', 'kaise add', 'colleague', 'bulk invite', 'person add'],
    workflowId: 'user-invite',
    onboarding: {
      tier: 'critical',
      order: 2,
      title: 'Invite your team',
      description: 'Add team members so they can start using the workspace',
      completionKey: 'account.totalUsers',
      completionCheck: 'count',
      completionThreshold: 2,
      estimatedMinutes: 2,
      adminOnly: true,
      showInTrial: true,
      showInFullSetup: true
    }
  },
  {
    id: 'remove_user',
    text: 'Remove User',
    tooltip: 'Remove or deactivate a user',
    icon: 'person_remove',
    category: 'admin',
    featureCategory: 'Team & Users',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/users',
    videoUrl: 'videos/admin/remove-user.webm',
    web: {
      videoUrl: 'videos/admin/remove-user.webm'
    },
    keywords: ['delete', 'remove', 'user', 'hatao', 'hatana', 'delete karo', 'user hatao', 'remove user', 'deactivate'],
    workflowId: 'user-remove-or-inactivate'
  },
  {
    id: 'manage_users_teams',
    text: 'Manage Users & Teams',
    tooltip: 'Open Users & Team Management page',
    icon: 'people',
    category: 'admin',
    featureCategory: 'Team & Users',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/users',
    videoUrl: 'videos/admin/manage-users-teams.webm',
    web: {
      videoUrl: 'videos/admin/manage-users-teams.webm'
    },
    discoverable: {
      id: 'settings-team',
      route: '/admin',
      title: 'Manage team members and permissions',
      description: 'Invite users, assign roles, and control access to features',
      icon: 'people',
      priority: 90,
      videoUrl: 'videos/admin/manage-users-teams.webm',
      requiredRoles: ['admin']
    },
    onboardingRelevant: true
  },
  {
    id: 'view_products',
    text: 'Products & Services',
    tooltip: 'Configure your offerings',
    icon: 'inventory_2',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/products',
    videoUrl: 'videos/admin/view-products.webm',
    web: {
      videoUrl: 'videos/admin/view-products.webm'
    },
    keywords: ['product', 'service', 'catalog', 'inventory', 'products list', 'view products', 'product dekhein', 'service list', 'catalogue']
  },
  {
    id: 'get_started',
    text: 'Get Started Checklist',
    tooltip: 'Step-by-step onboarding wizard with 16 setup tasks — invite users, configure permissions, customize call outcomes, set up integrations, import leads, and train your team',
    icon: 'flag',
    category: 'admin',
    featureCategory: 'Help & Tutorials',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/home/get-started',
    keywords: ['onboarding', 'setup', 'get started', 'checklist', 'wizard', 'first time', 'configure workspace', 'initial setup'],
    videoUrl: 'videos/admin/get-started.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "Click here to get started in simple steps" in the top banner',
      'Follow the step-by-step wizard on the left sidebar',
      'Complete each step: invite users, set permissions, customize outcomes, etc.',
      'Mark each step as Done when complete — progress is saved automatically'
    ],
    web: { videoUrl: 'videos/admin/get-started.webm' }
  },
  {
    id: 'create_template',
    text: 'Create Templates',
    tooltip: 'Build WhatsApp/email templates',
    icon: 'message',
    category: 'admin',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreateTemplateComponent,
    dialogType: DialogType.FULL_MODAL,
    route: '/templates'
  },
  {
    id: 'create_whatsapp_broadcast',
    text: 'Send WhatsApp Broadcast',
    tooltip: 'Create and send WhatsApp Business API broadcast campaign to selected contacts',
    icon: 'campaign',
    category: 'user',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: MANAGER_PLUS,
    component: CreateWabaCampaignComponent,
    dialogType: DialogType.FULL_MODAL,
    closeOnClickOutside: false,
    keywords: ['whatsapp', 'broadcast', 'bulk message', 'campaign', 'waba']
  },
  {
    id: 'add_product',
    text: 'Add Product/Service',
    tooltip: 'Create a new product or service offering to include in quotations and deals',
    icon: 'add_shopping_cart',
    category: 'user',
    featureCategory: 'Settings & Customization',
    allowedRoles: ADMIN_ONLY,
    requiredCapability: 'catalogEdit',
    component: AddProductComponent,
    dialogType: DialogType.FULL_MODAL,
    closeOnClickOutside: false,
    videoUrl: 'videos/admin/add-product.webm',
    web: {
      videoUrl: 'videos/admin/add-product.webm'
    },
    keywords: ['product', 'service', 'catalog', 'inventory', 'add item', 'create product', 'naya product', 'product add karo', 'service add', 'item add']
  },
  {
    id: 'chart_gallery',
    text: 'Chart Gallery',
    tooltip: 'Browse and create custom charts and reports for metrics visualization',
    icon: 'bar_chart',
    category: 'user',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    component: ChartGalleryComponent,
    dialogType: DialogType.FULL_MODAL,
    closeOnClickOutside: true,
    keywords: ['chart', 'graph', 'report', 'analytics', 'visualization', 'metrics']
  },
  {
    id: 'video_calls',
    text: 'Video Calls',
    tooltip: 'View video call history and initiate new video meetings',
    icon: 'video_call',
    category: 'user',
    featureCategory: 'Communication & Calling',
    allowedRoles: ALL_ROLES,
    component: VideoCallsComponent,
    dialogType: DialogType.FULL_MODAL,
    closeOnClickOutside: false,
    keywords: ['video', 'call', 'meeting', 'conference', 'zoom']
  },
  {
    id: 'create_ticket_category',
    text: 'Create Ticket Category',
    tooltip: 'Define categories to organize support tickets — e.g., Billing, Technical, General Inquiry. Found under Customizations > Tickets tab',
    icon: 'category',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreateTicketCategoryComponent,
    dialogType: DialogType.SIDE_MODAL,
    closeOnClickOutside: false,
    keywords: ['ticket', 'category', 'support', 'issue type', 'ticket category', 'create category'],
    videoUrl: 'videos/admin/create-ticket-category.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "View All Customization" under Customize for Your Business',
      'Select the "Tickets" tab',
      'Click "+New Ticket Category"',
      'Enter the category name and save'
    ],
    web: { videoUrl: 'videos/admin/create-ticket-category.webm' }
  },
  {
    id: 'create_ticket_stage',
    text: 'Create Ticket Stage',
    tooltip: 'Define lifecycle stages for support tickets — e.g., Open, In Progress, Resolved, Closed. Found under Customizations > Tickets tab',
    icon: 'layers',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreateTicketStageComponent,
    dialogType: DialogType.SIDE_MODAL,
    closeOnClickOutside: false,
    keywords: ['ticket', 'stage', 'workflow', 'status', 'pipeline', 'ticket stage', 'create stage'],
    videoUrl: 'videos/admin/create-ticket-stage.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "View All Customization" under Customize for Your Business',
      'Select the "Tickets" tab',
      'Click "+New Ticket Stage"',
      'Enter the stage name and save'
    ],
    web: { videoUrl: 'videos/admin/create-ticket-stage.webm' }
  },
  {
    id: 'create_lead_tag',
    text: 'Create Lead Tag',
    tooltip: 'Create tags to categorize and filter leads — e.g., Hot Lead, Follow Up, VIP. Found under Customizations > Lead Tags tab',
    icon: 'label',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreateLeadTagComponent,
    dialogType: DialogType.SIDE_MODAL,
    closeOnClickOutside: false,
    keywords: ['tag', 'label', 'lead tag', 'categorize', 'organize', 'create tag'],
    videoUrl: 'videos/admin/create-lead-tag.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "View All Customization" under Customize for Your Business',
      'Select the "Lead Tags" tab',
      'Click "+New Lead Tag"',
      'Enter the tag name, choose a color, and save'
    ],
    web: { videoUrl: 'videos/admin/create-lead-tag.webm' }
  },
  {
    id: 'create_lead_source',
    text: 'Create Lead Source',
    tooltip: 'Add lead source channels to track where leads come from — e.g., Website, Referral, Event, Walk-in. Found under Customizations > Lead Source tab',
    icon: 'source',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreateLeadSourceComponent,
    dialogType: DialogType.SIDE_MODAL,
    closeOnClickOutside: false,
    keywords: ['lead source', 'channel', 'origin', 'attribution', 'tracking', 'create source'],
    videoUrl: 'videos/admin/create-lead-source.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "View All Customization" under Customize for Your Business',
      'Select the "Lead Source" tab',
      'Click "+New Lead Source"',
      'Enter the source name and save'
    ],
    web: { videoUrl: 'videos/admin/create-lead-source.webm' }
  },
  {
    id: 'create_document_category',
    text: 'Create Document Category',
    tooltip: 'Define categories for uploaded documents — e.g., Invoice, Agreement, ID Proof. Found under Customizations > Document Categories tab',
    icon: 'folder',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreateDocumentCategoryComponent,
    dialogType: DialogType.SIDE_MODAL,
    closeOnClickOutside: false,
    keywords: ['document category', 'file category', 'document type', 'organization', 'create document category'],
    videoUrl: 'videos/admin/create-document-category.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "View All Customization" under Customize for Your Business',
      'Select the "Document Categories" tab',
      'Click "+New Document Category"',
      'Enter the category name and save'
    ],
    web: { videoUrl: 'videos/admin/create-document-category.webm' }
  },
  {
    id: 'create_visit_outcome',
    text: 'Create Visit Outcome',
    tooltip: 'Define outcome types for field visits — e.g., Demo Given, Interested, Not Available. Found under Customizations > Visit Outcomes tab',
    icon: 'meeting_room',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreateVisitOutcomeComponent,
    dialogType: DialogType.SIDE_MODAL,
    closeOnClickOutside: false,
    keywords: ['visit outcome', 'meeting outcome', 'field visit', 'visit result', 'create visit outcome'],
    videoUrl: 'videos/admin/create-visit-outcome.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "View All Customization" under Customize for Your Business',
      'Select the "Visit Outcomes" tab',
      'Click "+New Visit Outcome"',
      'Enter the outcome name, configure next steps, and save'
    ],
    web: { videoUrl: 'videos/admin/create-visit-outcome.webm' }
  },
  {
    id: 'create_task_type',
    text: 'Create Task Type',
    tooltip: 'Define task categories your team uses — e.g., Follow-Up Call, Site Visit, Document Collection. Found under Customizations > Task Types tab',
    icon: 'assignment',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreateTaskTypeComponent,
    dialogType: DialogType.SIDE_MODAL,
    closeOnClickOutside: false,
    keywords: ['task type', 'task category', 'task definition', 'workflow', 'create task type'],
    videoUrl: 'videos/admin/create-task-type.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "View All Customization" under Customize for Your Business',
      'Select the "Task Types" tab',
      'Click "+New Task Type"',
      'Enter the task type name, configure settings, and save'
    ],
    web: { videoUrl: 'videos/admin/create-task-type.webm' }
  },
  {
    id: 'create_call_outcome',
    text: 'Create Call Outcome',
    tooltip: 'Define call disposition types — e.g., Interested, Not Interested, Callback, Wrong Number. Found under Customizations > Call Outcomes tab',
    icon: 'phone_in_talk',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreateCallOutcomeComponent,
    dialogType: DialogType.SIDE_MODAL,
    closeOnClickOutside: false,
    keywords: ['call outcome', 'call disposition', 'call result', 'phone outcome', 'create call outcome'],
    videoUrl: 'videos/admin/create-call-outcome.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "View All Customization" under Customize for Your Business',
      'Select the "Call Outcomes" tab',
      'Click "+New Call Outcome"',
      'Enter the outcome name, configure next steps and follow-up, and save'
    ],
    web: { videoUrl: 'videos/admin/create-call-outcome.webm' }
  },
  {
    id: 'create_deal_stage',
    text: 'Create Deal Stage',
    tooltip: 'Add stages to your sales pipeline — e.g., Qualified, Proposal Sent, Negotiation, Won, Lost. Found under Customizations > Deal Stages tab',
    icon: 'timeline',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreateDealStageComponent,
    dialogType: DialogType.SIDE_MODAL,
    closeOnClickOutside: false,
    keywords: ['deal stage', 'opportunity stage', 'pipeline', 'sales stage', 'funnel', 'create deal stage'],
    videoUrl: 'videos/admin/create-deal-stage.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "View All Customization" under Customize for Your Business',
      'Select the "Deal Stages" tab',
      'Click "+New Stage"',
      'Enter the stage name, set probability, and save'
    ],
    web: { videoUrl: 'videos/admin/create-deal-stage.webm' }
  },
  {
    id: 'query_builder',
    text: 'Advanced Query Builder',
    tooltip: 'Build complex filters and rules for contact segmentation and automation',
    icon: 'filter_alt',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    component: QueryBuilderComponent,
    dialogType: DialogType.FULL_MODAL,
    closeOnClickOutside: false,
    keywords: ['filter', 'query', 'segment', 'rules', 'conditions', 'advanced search']
  },
  {
    id: 'manually_select_contacts',
    text: 'Manual Contact Picker',
    tooltip: 'Manually select specific contacts for campaigns or bulk actions',
    icon: 'check_box',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    component: ManuallySelectContactsComponent,
    dialogType: DialogType.FULL_MODAL,
    closeOnClickOutside: false,
    keywords: ['pick contacts', 'select leads', 'manual selection', 'choose contacts']
  },
  {
    id: 'create_form',
    text: 'Create Form',
    tooltip: 'Create custom form',
    icon: 'list_alt',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreateFormComponent,
    dialogType: DialogType.FULL_MODAL,
    route: '/forms',
    workflowId: 'create-web-form'
  },
  {
    id: 'get_support_admin',
    text: 'Admin Actions',
    tooltip: 'Open legacy Admin Actions hub',
    icon: 'support_agent',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: AdminActionsComponent,
    dialogType: DialogType.FULL_MODAL,
    rootRoute: '/home'
  },
  {
    id: 'create_workspace',
    text: 'Create Workspace',
    tooltip: 'Start a new workspace',
    icon: 'domain_add',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreateTeamAccountComponent,
    dialogType: DialogType.FULL_MODAL,
    rootRoute: '/home'
  },
  {
    id: 'create_team',
    text: 'Create Team',
    tooltip: 'Set up teams & members with drag-and-drop interface',
    icon: 'groups',
    category: 'admin',
    featureCategory: 'Team & Users',
    allowedRoles: SUPERADMIN_ONLY,
    component: InviteToGroupNewComponent,
    dialogType: DialogType.FULL_MODAL,
    rootRoute: '/admin/users',
    videoUrl: 'videos/admin/create-team.webm',
    web: {
      videoUrl: 'videos/admin/create-team.webm'
    },
    keywords: ['team', 'group', 'create team', 'new group', 'organize users', 'team banao', 'naya team', 'group banao', 'team create karo'],
    workflowId: 'team-create'
  },
  {
    id: 'create_property',
    text: 'Create Property',
    tooltip: 'Add custom property',
    icon: 'tune',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreatePropertyComponent,
    dialogType: DialogType.FULL_MODAL,
    rootRoute: '/admin/settings',
    workflowId: 'create-property',
    videoUrl: 'videos/admin/create-property.webm',
    web: {
      videoUrl: 'videos/admin/create-property.webm'
    },
    keywords: ['custom property', 'custom field', 'add field', 'property', 'custom attribute', 'naya field', 'property add karo', 'custom property banao']
  },
  {
    id: 'create_team_group',
    text: 'Create Team/Group',
    tooltip: 'Create a new team or group to organize users and assign permissions',
    icon: 'group_add',
    category: 'admin',
    featureCategory: 'Team & Users',
    allowedRoles: SUPERADMIN_ONLY,
    component: CreateGroupComponent,
    dialogType: DialogType.SIDE_MODAL,
    closeOnClickOutside: false,
    videoUrl: 'videos/admin/create-team.webm',
    web: {
      videoUrl: 'videos/admin/create-team.webm'
    },
    keywords: ['team', 'group', 'create team', 'new group', 'organize users', 'team banao', 'naya team', 'group banao', 'team create karo']
  },
  {
    id: 'view_team',
    text: 'View Teams',
    tooltip: 'Go to Teams tab',
    icon: 'diversity_3',
    category: 'admin',
    featureCategory: 'Team & Users',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/users?tab=teams',
    videoUrl: 'videos/admin/view-team.webm',
    web: {
      videoUrl: 'videos/admin/view-team.webm'
    },
    keywords: ['teams', 'view teams', 'team list', 'team dekhein', 'teams manage', 'team memberships']
  },
  {
    id: 'export_data',
    text: 'Export Data',
    tooltip: 'Export leads or activities',
    icon: 'file_download',
    category: 'admin',
    featureCategory: 'Reports & Analytics',
    allowedRoles: ADMIN_ONLY,
    route: '/leads/all-leads',
    keywords: ['export', 'download leads', 'csv', 'excel', 'data export', 'export karo', 'leads download', 'bulk export', 'export data'],
    videoUrl: 'videos/leads/export-data.webm',
    web: { videoUrl: 'videos/leads/export-data.webm' },
  },
  {
    id: 'view_export',
    text: 'View Exports',
    tooltip: 'Download processed exports',
    icon: 'folder',
    category: 'admin',
    featureCategory: 'Reports & Analytics',
    allowedRoles: ADMIN_ONLY,
    route: '/export/exports',
    keywords: ['view export', 'download export', 'data exports', 'export history', 'past exports', 'export dekho'],
    videoUrl: 'videos/leads/export-data.webm',
    web: { videoUrl: 'videos/leads/export-data.webm' },
  },
  // customize_table removed — duplicate of hide_show_columns
  {
    id: 'customize_table_assign',
    text: 'Customize Assign Table',
    tooltip: 'Adjust columns in assign/reassign view',
    icon: 'table_chart',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    route: '/leads/all-leads',
    keywords: ['assign table', 'assignment columns', 'customize assign'],
    steps: [
      'Open Assign Leads toolbar',
      'Click column settings icon',
      'Toggle or reorder columns as needed',
      'Save view for future use'
    ]
  },
  {
    id: 'filter_table',
    text: 'Filter Lead Table',
    tooltip: 'Apply advanced filters to the grid',
    icon: 'filter_list',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    keywords: ['filter leads', 'grid filter', 'advanced filter', 'search lead', 'my leads', 'smart view', 'filter karo', 'lead dhundo', 'stage filter', 'column filter'],
    videoUrl: 'videos/leads/filter-table.webm',
    web: { videoUrl: 'videos/leads/filter-table.webm' },
    steps: [
      'Click the filter icon or press "/"',
      'Add property, operator, and value',
      'Apply filter and optionally save as smart view'
    ],
    discoverable: {
      id: 'leads-advanced-filter',
      route: '/leads/all-leads',
      title: 'Filter leads with advanced conditions',
      description: 'Build multi-condition filters by property, operator, and value — save as Smart View for reuse',
      icon: 'filter_list',
      priority: 80,
      videoUrl: 'videos/leads/filter-table.webm'
    }
  },
  {
    id: 'view_template',
    text: 'View Templates',
    tooltip: 'Open message template library',
    icon: 'library_books',
    category: 'admin',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/templates',
    keywords: ['templates', 'message template', 'whatsapp template', 'waba template', 'email template', 'template dekho', 'template library', 'snippets', 'sync templates'],
    videoUrl: 'videos/admin/view-template.webm',
    web: { videoUrl: 'videos/admin/view-template.webm' },
  },
  {
    id: 'link_form',
    text: 'Link Form',
    tooltip: 'Link forms to workflows',
    icon: 'link',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/forms',
    keywords: ['form', 'custom form', 'link form', 'create form', 'form banao', 'form attach', 'call outcome form', 'visit outcome form', 'task form', 'deal stage form', 'form link karo'],
    videoUrl: 'videos/admin/link-form.webm',
    web: { videoUrl: 'videos/admin/link-form.webm' },
  },
  {
    id: 'view_metrics',
    text: 'View Metrics',
    tooltip: 'Open performance dashboard',
    icon: 'insights',
    category: 'admin',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    route: '/metrics/performance',
    keywords: ['metrics', 'performance', 'analytics', 'dashboard', 'report', 'calls report', 'team performance', 'user metrics', 'performance dekho', 'metrics dashboard', 'export metrics', 'save view'],
    videoUrl: 'videos/metrics/view-metrics.webm',
    web: { videoUrl: 'videos/metrics/view-metrics.webm' },
  },
  {
    id: 'view_source_reports',
    text: 'Source Reports',
    tooltip: 'Analyze lead performance by source — see which channels bring the best leads',
    icon: 'source',
    category: 'admin',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    route: '/metrics/source-report',
    keywords: ['source report', 'lead source', 'channel performance', 'source wise', 'source analysis', 'kis source se lead aaye'],
    videoUrl: 'videos/metrics/view-source-reports.webm',
    web: { videoUrl: 'videos/metrics/view-source-reports.webm' },
    steps: [
      'Go to Metrics from the top navigation bar',
      'Click "Source Reports" in the contextual toolbar',
      'Filter by Sources, Uploads, or Tags',
      'Set time period and filter by team/user',
      'View source-wise lead position table',
      'Click any cell to drill down to underlying leads'
    ]
  },
  {
    id: 'view_attendance',
    text: 'Attendance',
    tooltip: 'Track team attendance — calendar view with check-in/out times, location, and face capture',
    icon: 'event_available',
    category: 'admin',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    route: '/metrics/attendance',
    keywords: ['attendance', 'check in', 'check out', 'hajri', 'attendance report', 'team attendance', 'leave'],
    videoUrl: 'videos/metrics/view-attendance.webm',
    web: { videoUrl: 'videos/metrics/view-attendance.webm' },
    steps: [
      'Go to Metrics from the top navigation bar',
      'Click "Attendance Metrics" in the contextual toolbar',
      'View the calendar with color-coded attendance (Green/Red/Yellow)',
      'Click a user in the sidebar for individual view',
      'Click a day to see check-in/out time, face capture, location',
      'Export to Excel from the calendar header'
    ]
  },
  {
    id: 'view_call_recordings',
    text: 'Call Recordings',
    tooltip: 'Listen to recorded calls — filter for recordings only, play inline with speed control, and download',
    icon: 'record_voice_over',
    category: 'admin',
    featureCategory: 'Communication & Calling',
    allowedRoles: MANAGER_PLUS,
    route: '/engagements/calls',
    keywords: ['call recordings', 'recorded calls', 'listen calls', 'play recording', 'call playback', 'download recording', 'call recording sunno', 'recording filter'],
    videoUrl: 'videos/engagements/view-call-recordings.webm',
    web: { videoUrl: 'videos/engagements/view-call-recordings.webm' },
    steps: [
      'Go to Engagements from the top navigation bar',
      'Click "Calls" in the contextual toolbar',
      'Turn on the "Recording" toggle to filter for calls with recordings',
      'Click the play button on any row to listen inline',
      'Use speed control (1x / 1.5x / 2x) and seek bar',
      'Select a row and click "Download Recording" to save'
    ]
  },
  {
    id: 'create_distribution_dynamic',
    text: 'Split Lead Distribution',
    tooltip: 'Route different leads to different people based on conditions',
    icon: 'share',
    category: 'admin',
    featureCategory: 'Campaigns & Automation',
    allowedRoles: SUPERADMIN_ONLY,
    component: DefineDistributionComponent,
    dialogType: DialogType.FULL_MODAL,
    rootRoute: '/leads',
    videoUrl: 'videos/leads/define-distribution.webm',
    web: {
      videoUrl: 'videos/leads/define-distribution.webm'
    },
    workflowId: 'leads-distribution-automation'
  },
  {
    id: 'edit_user',
    text: 'Edit Profile',
    tooltip: 'Update your profile picture, name (inline edit), registered email, and phone number — access via account avatar dropdown in top bar',
    icon: 'manage_accounts',
    category: 'user',
    featureCategory: 'Team & Users',
    allowedRoles: ALL_ROLES,
    route: '/account/profile',
    keywords: ['edit profile', 'change name', 'update email', 'change phone', 'profile picture', 'avatar', 'my profile'],
    videoUrl: 'videos/account/edit-user.webm',
    steps: [
      'Click your account name/avatar in the top-right corner',
      'Select "Profile & Preferences" from the dropdown',
      'Click your name to edit inline — changes save on blur',
      'Click edit icon next to email or phone to update',
      'Click pencil icon on profile picture to upload a new one'
    ],
    web: { videoUrl: 'videos/account/edit-user.webm' }
  },
  {
    id: 'integrations',
    text: 'Open Integrations',
    tooltip: 'Browse and connect WhatsApp, telephony, lead sources (IndiaMART, Facebook, Google), marketplaces, CRMs, and API integrations — filter by category, see connection status, and manage active integrations',
    icon: 'integration_instructions',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/integrations',
    keywords: ['integrations', 'connect', 'whatsapp', 'telephony', 'lead source', 'facebook', 'google', 'indiamart', 'api', 'webhook', 'crm sync'],
    videoUrl: 'videos/admin/integrations.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "View All Integrations" under 🔗 Integrations',
      'Filter by category: All, Connected, Frequent, or specific types',
      'Each card shows logo, status, and Connect/Manage buttons',
      'Click Connect to set up a new integration or Manage to configure existing ones'
    ],
    web: { videoUrl: 'videos/admin/integrations.webm' },
    discoverable: {
      id: 'settings-integrations',
      route: '/admin',
      title: 'Connect third-party integrations',
      description: 'Sync with email, calendar, CRM, and other business tools',
      icon: 'integration_instructions',
      priority: 80,
      videoUrl: 'videos/admin/integrations.webm',
      actionId: 'integrations'
    },
    onboardingRelevant: true
  },
  {
    id: 'account_details',
    text: 'Account Basics',
    tooltip: 'View and edit workspace account details — Account Name, Business Name, Website, Industry, Product/Service, Account Status, and Device Registration Key (SuperAdmin)',
    icon: 'badge',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:basic-detail)',
    keywords: ['account details', 'organization info', 'workspace name', 'business name', 'industry', 'registration key', 'account status'],
    videoUrl: 'videos/admin/account-details.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "Modify Workspace/Account Detail" under 👤 Account',
      'View Account Name, Business Name, Website, Industry, and Status',
      'SuperAdmin can click "Edit Account Detail" to modify workspace info',
      'Use the settings sidebar to navigate to other admin sections'
    ],
    web: { videoUrl: 'videos/admin/account-details.webm' }
  },
  {
    id: 'manage_access',
    text: 'Role-Based Access',
    tooltip: 'Manage role-based permissions and access controls for different user roles',
    icon: 'admin_panel_settings',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['permissions', 'access control', 'roles', 'security', 'user access'],
    videoUrl: 'videos/admin/manage-permissions.webm',
    web: { videoUrl: 'videos/admin/manage-permissions.webm' },
  },
  // === PERMISSION HELPER ACTIONS ===
  // These help users find specific permission toggles via natural language queries
  {
    id: 'permission_mask_numbers',
    text: 'Mask Phone Numbers',
    tooltip: 'Navigate to permissions to enable/disable phone number masking (prevents SIM-based calling)',
    icon: 'phone_locked',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['mask numbers', 'mask phone numbers', 'hide numbers', 'privacy', 'phone security', 'number masking', 'mask mobile', 'conceal numbers'],
    videoUrl: 'videos/admin/manage-permissions.webm',
    web: { videoUrl: 'videos/admin/manage-permissions.webm' },
  },
  {
    id: 'permission_disable_web',
    text: 'Disable Web Access',
    tooltip: 'Navigate to permissions to restrict web access and force app-only usage',
    icon: 'web_asset_off',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['disable web', 'disable web access', 'block web', 'app only', 'restrict browser', 'force mobile app'],
    videoUrl: 'videos/admin/manage-permissions.webm',
    web: { videoUrl: 'videos/admin/manage-permissions.webm' },
  },
  {
    id: 'permission_data_export',
    text: 'Data Export Permissions',
    tooltip: 'Navigate to permissions to control who can export contact data',
    icon: 'file_download',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['data export', 'export permission', 'download data', 'export contacts', 'export leads'],
    videoUrl: 'videos/admin/manage-permissions.webm',
    web: { videoUrl: 'videos/admin/manage-permissions.webm' },
  },
  {
    id: 'permission_mask_exports',
    text: 'Mask Numbers in Exports',
    tooltip: 'Navigate to permissions to mask phone numbers in exported data files',
    icon: 'shield',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['mask export', 'hide numbers in export', 'export without numbers', 'secure export', 'privacy export'],
    videoUrl: 'videos/admin/manage-permissions.webm',
    web: { videoUrl: 'videos/admin/manage-permissions.webm' },
  },
  {
    id: 'permission_user_directory',
    text: 'User Directory Access',
    tooltip: 'Navigate to permissions to control whether users can view other employees contact info',
    icon: 'contacts',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['user directory', 'employee directory', 'team contacts', 'hide employee info', 'staff directory'],
    videoUrl: 'videos/admin/manage-permissions.webm',
    web: { videoUrl: 'videos/admin/manage-permissions.webm' },
  },
  {
    id: 'permission_hide_sources',
    text: 'Hide Lead Sources',
    tooltip: 'Navigate to permissions to hide lead source information from team members',
    icon: 'visibility_off',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['hide lead source', 'hide source', 'conceal origin', 'hide channel', 'source privacy'],
    videoUrl: 'videos/admin/manage-permissions.webm',
    web: { videoUrl: 'videos/admin/manage-permissions.webm' },
  },
  {
    id: 'permission_bulk_upload',
    text: 'Bulk Upload Permission',
    tooltip: 'Navigate to permissions to control who can bulk upload leads',
    icon: 'upload_file',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['bulk upload permission', 'import permission', 'upload leads permission', 'csv upload'],
    videoUrl: 'videos/admin/manage-permissions.webm',
    web: { videoUrl: 'videos/admin/manage-permissions.webm' },
  },
  {
    id: 'permission_delete_leads',
    text: 'Delete Lead Permission',
    tooltip: 'Navigate to permissions to control who can delete leads',
    icon: 'delete',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['delete permission', 'remove leads', 'delete leads permission', 'lead deletion'],
    videoUrl: 'videos/admin/manage-permissions.webm',
    web: { videoUrl: 'videos/admin/manage-permissions.webm' },
  },
  {
    id: 'permission_assign_leads',
    text: 'Lead Assignment Permission',
    tooltip: 'Navigate to permissions to control who can assign leads to other members',
    icon: 'person_add_alt',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['assign permission', 'lead assignment', 'reassign leads', 'distribute leads'],
    videoUrl: 'videos/admin/manage-permissions.webm',
    web: { videoUrl: 'videos/admin/manage-permissions.webm' },
  },
  {
    id: 'permission_hide_activities',
    text: 'Activity Visibility Control',
    tooltip: 'Navigate to permissions to control what activities users can see on leads',
    icon: 'history',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['hide activities', 'activity privacy', 'hide history', 'conceal activities', 'activity visibility'],
    videoUrl: 'videos/admin/manage-permissions.webm',
    web: { videoUrl: 'videos/admin/manage-permissions.webm' },
  },
  {
    id: 'permission_track_calls',
    text: 'Call Tracking Settings',
    tooltip: 'Navigate to permissions to enable/disable automatic call tracking for unknown numbers',
    icon: 'call',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['track calls', 'call tracking', 'disable tracking', 'unknown numbers', 'auto log calls'],
    videoUrl: 'videos/admin/manage-permissions.webm',
    web: { videoUrl: 'videos/admin/manage-permissions.webm' },
  },
  {
    id: 'permission_camera_only',
    text: 'Camera-Only Image Capture',
    tooltip: 'Navigate to permissions to force camera capture only (disable gallery import) in mobile app',
    icon: 'photo_camera',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['camera only', 'disable gallery', 'force camera', 'photo capture', 'image policy'],
    videoUrl: 'videos/admin/manage-permissions.webm',
    web: { videoUrl: 'videos/admin/manage-permissions.webm' },
  },
  {
    id: 'do_not_track',
    text: 'Do Not Track List',
    tooltip: 'Manage do-not-track leads — block personal or spam numbers from being tracked',
    icon: 'block',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:do-not-track)',
    keywords: ['personal calls', 'do not track', 'exclude numbers', 'personal numbers', 'call privacy', 'untrack calls', 'private numbers', 'spam number block', 'number block karo', 'track mat karo'],
    videoUrl: 'videos/leads/do-not-track.webm',
    web: { videoUrl: 'videos/leads/do-not-track.webm' },
    steps: [
      'Go to Leads > Active Leads',
      'Select the personal or spam lead',
      'Click Delete',
      'Check "Put in blocked list. Do not track this in future."',
      'Confirm deletion',
      'To undo later: Settings > Do Not Track > Remove'
    ],
  },
  {
    id: 'active_leads',
    text: 'View Active Leads',
    tooltip: 'Open active leads table — leads currently in your pipeline (New, Prospecting, Qualified, Opportunity)',
    icon: 'leaderboard',
    category: 'admin',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ADMIN_ONLY,
    route: '/leads/all-leads',
    keywords: ['active leads', 'all leads', 'lead list', 'new leads', 'prospecting', 'pipeline', 'mere leads', 'active lead dikha'],
    videoUrl: 'videos/leads/active-inactive-leads.webm',
    web: { videoUrl: 'videos/leads/active-inactive-leads.webm' },
    steps: [
      'Go to Leads from the top navigation bar',
      'Click "Active Leads" in the contextual toolbar',
      'Use Smart View dropdown to filter (All Active, Uncontacted, Prospecting, etc.)',
      'Toggle "My" vs "All" for personal or team view',
      'Click column headers to sort, use filter bar for advanced filtering'
    ]
  },
  {
    id: 'quote_defaults',
    text: 'Quotation Defaults',
    tooltip: 'Configure quotation defaults',
    icon: 'request_quote',
    category: 'admin',
    featureCategory: 'Deals & Quotations',
    allowedRoles: SUPERADMIN_ONLY,
    videoUrl: 'videos/quotes/quote-defaults.webm',
    web: { videoUrl: 'videos/quotes/quote-defaults.webm' },
    component: QuotationDefaultsComponent,
    dialogType: DialogType.FULL_MODAL
  },
  {
    id: 'update_account',
    text: 'Update Workspace Details',
    tooltip: 'Modify account profile & tax info',
    icon: 'account_circle',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: UpdateAccountComponent,
    dialogType: DialogType.FULL_MODAL
  },
  {
    id: 'update_account_defaults',
    text: 'Workspace Defaults',
    tooltip: 'Edit default stages, statuses, etc.',
    icon: 'settings_applications',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    component: UpdateAccountDefaultsComponent,
    dialogType: DialogType.FULL_MODAL
  },
  // === LEADS/CONTACTS ROUTES ===
  {
    id: 'view_inactive_leads',
    text: 'Worked Leads',
    tooltip: 'View inactive or closed leads — Won, Lost, Manually Closed, Time Over. Can rechurn to reactivate.',
    icon: 'archive',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/inactive-leads',
    keywords: ['inactive', 'closed', 'archived', 'worked', 'old leads', 'won', 'lost', 'rechurn', 'band leads', 'purane leads'],
    videoUrl: 'videos/leads/active-inactive-leads.webm',
    web: { videoUrl: 'videos/leads/active-inactive-leads.webm' },
    steps: [
      'Go to Leads from the top navigation bar',
      'Click "Already Worked Leads" in the contextual toolbar',
      'Filter by category: Won, Lost, Manually Closed, Time Over, Negative Outcome',
      'Select leads and click "Rechurn" to move them back to Active'
    ]
  },
  {
    id: 'view_businesses',
    text: 'Business Accounts',
    tooltip: 'View and manage business/company accounts in an AG Grid — search, view linked leads, edit details, merge duplicates, and export business data',
    icon: 'business',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/businesses',
    keywords: ['companies', 'business', 'accounts', 'organizations', 'business list', 'company list', 'linked leads', 'merge businesses', 'export businesses'],
    videoUrl: 'videos/leads/view-businesses.webm',
    steps: [
      'Click Leads in the top navigation bar',
      'Select Businesses from the contextual toolbar',
      'View all businesses in the sortable grid (name, address, website, industry, priority)',
      'Search by business name using the search bar',
      'Select a row to see actions — View Leads, Link Leads, Edit, Export',
      'Customize visible columns with the Customize button'
    ],
    web: {
      videoUrl: 'videos/leads/view-businesses.webm'
    }
  },
  {
    id: 'view_documents',
    text: 'Lead Documents',
    tooltip: 'Document gallery — browse all uploaded files by category, filter by contact, upload new documents. Also accessible per-lead from the Documents tab in Lead Detail',
    icon: 'folder_open',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/documents',
    keywords: ['documents', 'files', 'uploads', 'attachments', 'document gallery', 'lead documents', 'upload file', 'pdf', 'proposal', 'invoice', 'contract'],
    videoUrl: 'videos/leads/view-documents.webm',
    steps: [
      'Click Leads in the top navigation bar',
      'Select Documents from the contextual toolbar',
      'Browse the document gallery with masonry grid layout',
      'Filter by category (Proposal, Invoice, Contract, ID) using chips',
      'Filter by contact using the contact selector dropdown',
      'Click +Add Document to upload a new file',
      'For per-lead documents, open a lead detail and click the Documents tab'
    ],
    web: {
      videoUrl: 'videos/leads/view-documents.webm'
    }
  },
  {
    id: 'view_contact_lists',
    text: 'Segments & Lists',
    tooltip: 'View all contact lists and segments — organize leads for campaigns and bulk operations',
    icon: 'list_alt',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/lists',
    keywords: ['lists', 'segments', 'groups', 'contact lists', 'lead list', 'list banao', 'segment'],
    videoUrl: 'videos/leads/view-contact-lists.webm',
    web: { videoUrl: 'videos/leads/view-contact-lists.webm' },
    steps: [
      'Go to Leads from the top navigation bar',
      'Click "Lists" in the contextual toolbar',
      'View all lists in the grid — switch between Lists and List Members tabs',
      'Click "Create List" to make a new segment with filters'
    ]
  },
  {
    id: 'create_contact_list',
    text: 'Create Contact List',
    tooltip: 'Create new contact list or segment with filters and conditions',
    icon: 'playlist_add',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    route: '/leads/create-list',
    keywords: ['create list', 'new segment', 'filter leads']
  },
  {
    id: 'view_distributions',
    text: 'View Distributions',
    tooltip: 'View all lead distribution rules — auto-assign incoming leads to team members',
    icon: 'alt_route',
    category: 'admin',
    featureCategory: 'Campaigns & Automation',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/leads/distributions',
    keywords: ['distribution', 'routing', 'auto-assign', 'lead assignment', 'round robin', 'lead distribute'],
    videoUrl: 'videos/leads/view-distributions.webm',
    web: { videoUrl: 'videos/leads/view-distributions.webm' },
    steps: [
      'Go to Leads from the top navigation bar',
      'Click "Distribute" in the contextual toolbar',
      'View all distribution rules in the grid',
      'Click "+ New Distribution" to create a new auto-assignment rule',
      'Select a rule to edit, copy, or delete'
    ]
  },
  {
    id: 'email_leads',
    text: 'Email-to-Lead',
    tooltip: 'View and manage leads captured via email integration (mailbox)',
    icon: 'email',
    category: 'admin',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ADMIN_ONLY,
    route: '/leads/mailbox',
    keywords: ['email leads', 'mailbox', 'email integration']
  },
  // === OPPORTUNITIES/DEALS ROUTES ===
  {
    id: 'view_deals_kanban',
    text: 'Deals Pipeline',
    tooltip: 'View opportunities in kanban board — drag-and-drop deal cards across sales stages',
    icon: 'view_kanban',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/opportunity-list',
    workflowId: 'view-deals-kanban',
    keywords: ['deals', 'pipeline', 'opportunities', 'kanban', 'sales stages', 'deal board', 'deals dikha'],
    videoUrl: 'videos/deals/view-deals-kanban.webm',
    web: { videoUrl: 'videos/deals/view-deals-kanban.webm' },
    steps: [
      'Go to Opportunities from the top navigation bar',
      'Click "Deals" in the contextual toolbar — Kanban view loads by default',
      'View deal cards organized by stage columns',
      'Drag cards to move deals between stages',
      'Use "My" / "All" toggle and search to filter deals'
    ],
    discoverable: {
      id: 'deals-kanban-view',
      route: '/opportunities/opportunity-list',
      title: 'Switch to kanban board view',
      description: 'Visualize your sales pipeline with drag-and-drop cards for each stage',
      icon: 'view_kanban',
      priority: 90,
      videoUrl: 'videos/deals/view-deals-kanban.webm'
    },
    onboardingRelevant: true,
    onboarding: {
      tier: 'important',
      order: 2,
      title: 'Explore your deals pipeline',
      description: 'See how deals flow through stages on the kanban board',
      estimatedMinutes: 2,
      showInTrial: true,
      showInFullSetup: true
    }
  },
  {
    id: 'view_deals_table',
    text: 'Deals Table',
    tooltip: 'View all opportunities in a sortable, filterable table — alternative to Kanban board',
    icon: 'table_chart',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/opportunity-table',
    keywords: ['deals table', 'opportunities grid', 'deal list', 'deal table view'],
    videoUrl: 'videos/deals/view-deals-table.webm',
    web: { videoUrl: 'videos/deals/view-deals-table.webm' },
    steps: [
      'Go to Opportunities from the top navigation bar',
      'Click "Deals Table" in the contextual toolbar',
      'View all deals in a sortable grid with columns for amount, stage, owner, dates',
      'Sort by column headers and use filters for advanced search'
    ]
  },
  {
    id: 'view_payments_card',
    text: 'Payment Cards',
    tooltip: 'View payment history as visual cards — transactions, invoices, and payment status at a glance',
    icon: 'payment',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/payments/card',
    keywords: ['payments', 'invoices', 'transactions', 'revenue', 'payment cards', 'payment dikha'],
    videoUrl: 'videos/deals/view-payments-card.webm',
    web: { videoUrl: 'videos/deals/view-payments-card.webm' },
    steps: [
      'Go to Opportunities from the top navigation bar',
      'Click "Payments" in the contextual toolbar — Card view is the default',
      'Filter by Status, Channel, Created date, and Paid date',
      'View summary totals: Total Paid, Cancelled, Requested'
    ]
  },
  {
    id: 'view_payments_table',
    text: 'Payments Table',
    tooltip: 'View payment history in a sortable table — with status, amounts, and bulk actions',
    icon: 'receipt_long',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/payments/table',
    keywords: ['payments table', 'invoice list', 'payment history', 'payment grid'],
    videoUrl: 'videos/deals/view-payments-table.webm',
    web: { videoUrl: 'videos/deals/view-payments-table.webm' },
    steps: [
      'Go to Opportunities from the top navigation bar',
      'Go to "Payments" then switch to Table view',
      'View all payments in a grid with Lead, Amount, Status, Payment Type columns',
      'Stats cards at top show Pending, Paid, and Cancelled totals',
      'Select a payment to Copy Link, Update, View History, or Cancel'
    ]
  },
  {
    id: 'view_quotes',
    text: 'Quotations',
    tooltip: 'View and manage all quotations — create, send, track status (Draft/Sent/Paid/Cancelled)',
    icon: 'request_quote',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/quotes',
    keywords: ['quotes', 'quotations', 'estimates', 'proposals', 'invoice', 'quote bhejo', 'quotation dikha'],
    videoUrl: 'videos/deals/view-quotes.webm',
    web: { videoUrl: 'videos/deals/view-quotes.webm' },
    steps: [
      'Go to Opportunities from the top navigation bar',
      'Navigate to Quotations',
      'Filter by status: All, Draft, Sent, Paid, Cancelled',
      'Select a quote to View PDF, Edit, Mark Paid, Copy & Create, or Cancel'
    ],
    discoverable: {
      id: 'quotes-builder',
      route: '/opportunities/quotes',
      title: 'Build professional quotations',
      description: 'Drag-and-drop products into quotes, apply discounts and taxes, then send or download as PDF',
      icon: 'request_quote',
      priority: 80,
      videoUrl: 'videos/deals/view-quotes.webm'
    }
  },
  // === TO-DO/TASKS ROUTES ===
  {
    id: 'view_tasks_kanban',
    text: 'Tasks Kanban',
    tooltip: 'View tasks on a kanban board — columns for Due Today, Upcoming, Overdue, and Done',
    icon: 'view_kanban',
    category: 'user',
    featureCategory: 'Tasks & Follow-ups',
    allowedRoles: ALL_ROLES,
    route: '/todo/to-do-kanban',
    keywords: ['tasks', 'todo', 'kanban', 'reminders', 'task board', 'kaam dikha', 'pending tasks'],
    videoUrl: 'videos/tasks/view-tasks-kanban.webm',
    web: { videoUrl: 'videos/tasks/view-tasks-kanban.webm' },
    steps: [
      'Click "To Do" in the top navigation bar',
      'View tasks organized in columns: Due Today, Follow up Today, Upcoming, Overdue, Done',
      'Drag task cards between columns to update status',
      'Use "My" / "All" toggle and search to filter tasks'
    ],
    discoverable: {
      id: 'tasks-kanban-board',
      route: '/todo/to-do-kanban',
      title: 'Organize tasks with drag-and-drop',
      description: 'View tasks on a kanban board — drag cards between Due Today, Upcoming, Overdue, and Done columns',
      icon: 'view_kanban',
      priority: 85,
      videoUrl: 'videos/tasks/view-tasks-kanban.webm'
    }
  },
  {
    id: 'view_tasks_grid',
    text: 'Tasks Table',
    tooltip: 'View all tasks in a sortable table — filter by overdue, today, upcoming, and done',
    icon: 'checklist',
    category: 'user',
    featureCategory: 'Tasks & Follow-ups',
    allowedRoles: ALL_ROLES,
    route: '/todo/grid',
    keywords: ['tasks table', 'todo list', 'task grid', 'task list', 'overdue tasks'],
    videoUrl: 'videos/tasks/view-tasks-grid.webm',
    web: { videoUrl: 'videos/tasks/view-tasks-grid.webm' },
    steps: [
      'Go to Engagements from the top navigation bar',
      'Click "Tasks" in the contextual toolbar — Table view loads',
      'Filter by task type: Due Today, Follow-ups, Overdue, Upcoming, Done',
      'Toggle "My" / "All" to switch views, export tasks for reporting'
    ]
  },
  // === ENGAGEMENT ROUTES ===
  {
    id: 'view_all_engagements',
    text: 'All Engagements',
    tooltip: 'View every customer interaction in one table — calls, tasks, visits, and messages with My/All toggle, search, bulk actions, export, and infinite scroll',
    icon: 'timeline',
    category: 'user',
    featureCategory: 'Communication & Calling',
    allowedRoles: ALL_ROLES,
    route: '/engagements/all-engagements',
    keywords: ['engagements', 'activities', 'interactions', 'touchpoints', 'all engagements', 'calls', 'tasks', 'visits', 'messages', 'engagement history'],
    videoUrl: 'videos/engagements/view-all-engagements.webm',
    steps: [
      'Click "Engagements" in the main navigation bar',
      'Select "All Engagements" from the contextual toolbar',
      'Toggle between "My" (yours) and "All" (team) engagements',
      'Use search to filter by task type, call outcome, notes, etc.',
      'Select rows for bulk actions like Complete Task or Assign',
      'Export engagement data to Excel/CSV'
    ],
    web: { videoUrl: 'videos/engagements/view-all-engagements.webm' },
    discoverable: {
      id: 'engagements-unified-view',
      route: '/engagements/all-engagements',
      title: 'View all interactions in one place',
      description: 'See every call, task, visit, and message across your team in a single timeline',
      icon: 'timeline',
      priority: 85,
      videoUrl: 'videos/engagements/view-all-engagements.webm'
    }
  },
  {
    id: 'view_video_calls',
    text: 'Video Calls',
    tooltip: 'View video call history — filter by platform, status, and schedule new meetings',
    icon: 'videocam',
    category: 'user',
    featureCategory: 'Communication & Calling',
    allowedRoles: ALL_ROLES,
    route: '/engagements/video-calls',
    keywords: ['video calls', 'video meetings', 'online meetings', 'google meet', 'video call history'],
    videoUrl: 'videos/engagements/view-video-calls.webm',
    web: { videoUrl: 'videos/engagements/view-video-calls.webm' },
    steps: [
      'Go to Engagements from the top navigation bar',
      'Navigate to Video Calls',
      'View call cards with status, platform, and participants',
      'Toggle "My" / "All" and filter by Platform, Status, Date',
      'Click "Schedule Video Call" to set up a new meeting'
    ]
  },
  {
    id: 'view_messages',
    text: 'SMS Messages',
    tooltip: 'View SMS messaging log — track sent, delivered, and failed messages with delivery status',
    icon: 'sms',
    category: 'user',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: ALL_ROLES,
    route: '/engagements/messages',
    keywords: ['sms', 'text messages', 'bulk sms', 'message log', 'sms history', 'message dikha'],
    videoUrl: 'videos/engagements/view-messages.webm',
    web: { videoUrl: 'videos/engagements/view-messages.webm' },
    steps: [
      'Go to Engagements from the top navigation bar',
      'Click "Messages" in the contextual toolbar',
      'View message grid with delivery status',
      'Filter by message type using chips, toggle "My" / "All"',
      'Search messages and export for reporting'
    ]
  },
  {
    id: 'view_visits',
    text: 'Field Visits',
    tooltip: 'Track in-person meetings and field visits — log outcomes, view visit history, and export',
    icon: 'directions_walk',
    category: 'user',
    featureCategory: 'Communication & Calling',
    allowedRoles: ALL_ROLES,
    route: '/engagements/visits',
    keywords: ['visits', 'meetings', 'field visits', 'in-person', 'visit log', 'visit history', 'meeting dikha'],
    videoUrl: 'videos/engagements/view-visits.webm',
    web: { videoUrl: 'videos/engagements/view-visits.webm' },
    steps: [
      'Go to Engagements from the top navigation bar',
      'Click "Visits" in the contextual toolbar',
      'View visit records with contact, type, outcome, date',
      'Filter by visit type using chips, toggle "My" / "All"',
      'Select a visit to log or modify outcome'
    ]
  },
  // === CONVERSATIONS ROUTES ===
  {
    id: 'view_lead_inbox',
    text: 'Lead Inbox',
    tooltip: 'View and manage all WhatsApp conversations with leads — filter by Smart Views, switch between My and All conversations, and reply directly from the inbox',
    icon: 'inbox',
    category: 'user',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: ALL_ROLES,
    route: '/conversations/inbox',
    keywords: ['inbox', 'whatsapp', 'messages', 'chat', 'lead inbox', 'conversations', 'smart views', 'my conversations', 'all conversations', 'whatsapp inbox'],
    videoUrl: 'videos/conversations/view-lead-inbox.webm',
    steps: [
      'Click Conversations in the top navigation bar',
      'Select Lead Inbox from the dropdown',
      'Use Smart View cards to filter conversations by status (Open, Pending, Resolved)',
      'Toggle between My and All to see your conversations vs everyone\'s',
      'Click any conversation row to open the chat thread'
    ],
    web: {
      videoUrl: 'videos/conversations/view-lead-inbox.webm'
    },
    onboarding: {
      tier: 'helpful',
      order: 1,
      title: 'Check your lead inbox',
      description: 'View and reply to WhatsApp conversations with leads',
      estimatedMinutes: 2,
      showInTrial: true,
      showInFullSetup: true
    },
    discoverable: {
      id: 'inbox-whatsapp-hub',
      route: '/conversations/inbox',
      title: 'Manage all WhatsApp conversations',
      description: 'View, filter, and reply to customer chats from one inbox — use Smart Views to stay organized',
      icon: 'inbox',
      priority: 90,
      videoUrl: 'videos/conversations/view-lead-inbox.webm'
    }
  },
  {
    id: 'view_team_inbox',
    text: 'Team Inbox',
    tooltip: 'Internal team messaging hub — start new chats with team members, view conversation history, and collaborate within your organization',
    icon: 'group',
    category: 'user',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: ALL_ROLES,
    route: '/conversations/team-inbox',
    keywords: ['team chat', 'internal messages', 'team inbox', 'team conversations', 'new chat', 'internal communication', 'team messaging'],
    videoUrl: 'videos/conversations/view-team-inbox.webm',
    steps: [
      'Click Conversations in the top navigation bar',
      'Select Team Inbox from the dropdown',
      'View your internal team conversations in the grid',
      'Click + New Chat to start a conversation with a team member',
      'Click any row to open the chat thread'
    ],
    web: {
      videoUrl: 'videos/conversations/view-team-inbox.webm'
    }
  },
  {
    id: 'view_broadcasts',
    text: 'WhatsApp Broadcasts',
    tooltip: 'View and manage all WhatsApp broadcast campaigns — see campaign status (Sent, Scheduled, Draft), delivery stats, and create new broadcast campaigns',
    icon: 'send',
    category: 'user',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: MANAGER_PLUS,
    route: '/conversations/broadcast',
    keywords: ['broadcast', 'whatsapp blast', 'bulk whatsapp', 'campaigns', 'broadcast list', 'whatsapp campaign', 'mass message', 'bulk message', 'scheduled broadcast'],
    videoUrl: 'videos/conversations/view-broadcasts.webm',
    steps: [
      'Click Conversations in the top navigation bar',
      'Select Broadcasts from the dropdown',
      'View all campaigns with status chips (Sent, Scheduled, Draft)',
      'Click + New Campaign to create a new broadcast',
      'Click any campaign row to view delivery details and stats'
    ],
    web: {
      videoUrl: 'videos/conversations/view-broadcasts.webm'
    }
  },
  {
    id: 'view_auto_reply',
    text: 'Auto Reply',
    tooltip: 'Set up automated WhatsApp reply rules — configure auto-replies for new conversations, missed calls, tasks, and custom triggers with campaign delivery stats',
    icon: 'auto_mode',
    category: 'admin',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/conversations/reply',
    keywords: ['auto reply', 'automated messages', 'chatbot', 'auto messaging', 'auto response', 'whatsapp auto reply', 'custom auto reply', 'campaign stats'],
    videoUrl: 'videos/conversations/view-auto-reply.webm',
    steps: [
      'Click Conversations in the top navigation bar',
      'Select Auto Messaging from the contextual toolbar',
      'Use category chips to filter reply types',
      'Click Set Up on any reply card to configure the auto-reply',
      'Click + Custom Auto-Reply to create a custom rule',
      'View campaign stats (Sent, Delivered, Read, Replied) in the inline table'
    ],
    web: {
      videoUrl: 'videos/conversations/view-auto-reply.webm'
    }
  },
  {
    id: 'view_chatbots',
    text: 'AI Chatbots',
    tooltip: 'Create and manage AI chatbots — configure knowledge bases, set up channel routing (WhatsApp, Instagram, FB Messenger), define custom actions, and test bot responses',
    icon: 'smart_toy',
    category: 'admin',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/conversations/chatbot',
    keywords: ['chatbot', 'ai assistant', 'automated support', 'ai chatbot', 'knowledge base', 'chat agent', 'whatsapp bot', 'instagram bot', 'channel routing'],
    videoUrl: 'videos/conversations/view-chatbots.webm',
    steps: [
      'Click Conversations in the top navigation bar',
      'Select AI Chatbots from the contextual toolbar',
      'View bot cards with channel toggles (WhatsApp, Instagram, FB Messenger)',
      'Click New Chat Agent to create a new bot',
      'Switch to Knowledge tab to manage documents and data sources',
      'Switch to Actions tab to define custom bot triggers'
    ],
    web: {
      videoUrl: 'videos/conversations/view-chatbots.webm'
    },
    discoverable: {
      id: 'chatbot-ai-automation',
      route: '/conversations/chatbot',
      title: 'Automate support with AI chatbots',
      description: 'Create AI bots with custom knowledge bases — deploy to WhatsApp, Instagram, or Facebook Messenger',
      icon: 'smart_toy',
      priority: 80,
      videoUrl: 'videos/conversations/view-chatbots.webm',
      requiredRoles: ['admin']
    }
  },
  {
    id: 'ai_chatbot_setup',
    text: 'AI Chatbot Setup',
    tooltip: 'Create a chatbot using a conversational AI assistant — describe your business and the AI will generate a complete bot configuration with guidelines, actions, and behavior',
    icon: 'auto_awesome',
    category: 'admin',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/conversations/chatbot',
    keywords: ['chatbot setup', 'create chatbot', 'ai bot', 'chatbot wizard', 'bot assistant', 'ai setup', 'configure chatbot', 'new chatbot'],
    steps: [
      'Open the AI Chatbot Setup assistant',
      'Describe your business and what you want the chatbot to do',
      'Answer 1-2 clarifying questions from the AI',
      'Review the generated bot configuration',
      'Click Open in Editor to review and save each bot'
    ],
  },
  {
    id: 'view_tickets',
    text: 'Support Tickets',
    tooltip: 'Kanban-style ticket board — view, drag, and manage customer support tickets across pipeline stages. Create new tickets and track resolution progress',
    icon: 'confirmation_number',
    category: 'user',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: ALL_ROLES,
    route: '/conversations/tickets',
    keywords: ['tickets', 'support', 'help desk', 'customer service', 'kanban', 'ticket board', 'support pipeline', 'ticket management', 'drag and drop tickets'],
    videoUrl: 'videos/conversations/view-tickets.webm',
    steps: [
      'Click Conversations in the top navigation bar',
      'Select Tickets from the dropdown',
      'View tickets in a kanban board organized by pipeline stages',
      'Drag and drop ticket cards between stages to update status',
      'Click + Ticket to create a new support ticket',
      'Click any ticket card to view its details'
    ],
    web: {
      videoUrl: 'videos/conversations/view-tickets.webm'
    },
    discoverable: {
      id: 'tickets-kanban-board',
      route: '/conversations/tickets',
      title: 'Track support tickets on a kanban board',
      description: 'Drag-and-drop tickets across pipeline stages — create, assign, and resolve customer issues visually',
      icon: 'confirmation_number',
      priority: 80,
      videoUrl: 'videos/conversations/view-tickets.webm'
    }
  },
  {
    id: 'create_broadcast',
    text: 'Create Broadcast',
    tooltip: 'Create new WhatsApp Business API broadcast campaign',
    icon: 'add_box',
    category: 'user',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: MANAGER_PLUS,
    route: '/conversations/create-broadcast',
    workflowId: 'create-broadcast',
    keywords: ['new broadcast', 'create whatsapp campaign']
  },
  {
    id: 'conversation_stats',
    text: 'Conversation Analytics',
    tooltip: 'View detailed conversation statistics, response times, and engagement metrics',
    icon: 'analytics',
    category: 'admin',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    route: '/conversations/stats',
    keywords: ['conversation stats', 'whatsapp analytics', 'message metrics']
  },
  // === ACTIVITIES ROUTES ===
  {
    id: 'view_activities_table',
    text: 'Activities Table',
    tooltip: 'View all CRM activities (calls, meetings, notes, tasks) in a sortable table — toggle between My and All activities, and export data for reporting',
    icon: 'list',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/activities/activities',
    keywords: ['activities', 'activity log', 'user actions', 'activities table', 'my activities', 'all activities', 'export activities', 'call log', 'meeting log'],
    videoUrl: 'videos/activities/view-activities-table.webm',
    steps: [
      'Click Engagements in the top navigation bar',
      'Select Activities Table View from the dropdown',
      'View all CRM activities in the sortable grid',
      'Toggle between My and All to see your activities vs everyone\'s',
      'Use the Export button to download activity data'
    ],
    web: {
      videoUrl: 'videos/activities/view-activities-table.webm'
    }
  },
  {
    id: 'view_activity_feed',
    text: 'Activity Feed',
    tooltip: 'Visual timeline feed showing all recent CRM activities — filter by activity type using lens categories, and view detailed event cards with timestamps',
    icon: 'rss_feed',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/activities/activities-feed',
    keywords: ['feed', 'timeline', 'activity stream', 'activity feed', 'recent activities', 'activity timeline', 'lens filter', 'event cards'],
    videoUrl: 'videos/activities/view-activity-feed.webm',
    steps: [
      'Click Engagements in the top navigation bar',
      'Select All Activities from the dropdown',
      'View the activity timeline feed with event cards',
      'Use the left-panel lens categories to filter by activity type',
      'Scroll through the timeline to see older events'
    ],
    web: {
      videoUrl: 'videos/activities/view-activity-feed.webm'
    }
  },
  // === ADMIN SETTINGS ROUTES ===
  {
    id: 'view_properties',
    text: 'Custom Properties',
    tooltip: 'Create and manage custom fields for Leads, Deals, Contacts, and Companies — define field types (text, number, date, dropdown), set display order, and edit property definitions',
    icon: 'playlist_add_check',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:properties)',
    keywords: ['custom fields', 'properties', 'custom attributes', 'new property', 'field types', 'display order', 'property definition', 'lead fields', 'deal fields'],
    videoUrl: 'videos/admin/view-properties.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "Create New Custom Property" under Customize for Your Business',
      'Or navigate to Admin > Settings > Properties in the sidebar',
      'Filter properties by object type (Lead, Deal, Contact, Company)',
      'Click + New Property to create a custom field',
      'Select rows to Edit Property Definition, set Display Order, or Delete'
    ],
    web: {
      videoUrl: 'videos/admin/view-properties.webm'
    }
  },
  {
    id: 'view_customizations_tab',
    text: 'Customizations',
    tooltip: 'Configure CRM workflows — Lead Lifecycle, Lead Tags, Lead Sources, Call Outcomes, Visit Outcomes, Task Types, Deal Stages, Ticket categories, and industry presets',
    icon: 'build',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:customizations)',
    keywords: ['customizations', 'workflow', 'outcomes', 'stages', 'lead lifecycle', 'lead tags', 'lead source', 'call outcomes', 'visit outcomes', 'task types', 'deal stages', 'ticket categories', 'industry preset'],
    videoUrl: 'videos/admin/view-customizations.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "Customize Call Outcomes, Tasks" or "View All Customization"',
      'Browse 14+ tabs: Preset, Lead Lifecycle, Lead Tags, Sources, Call/Visit Outcomes, Task Types, Deal Stages, Tickets',
      'Choose an industry template (Real Estate, Loans, Education, etc.) from Preset tab',
      'Create and manage color-coded tags, outcomes, and pipeline stages'
    ],
    web: {
      videoUrl: 'videos/admin/view-customizations.webm'
    }
  },
  {
    id: 'view_template_gallery',
    text: 'Template Gallery',
    tooltip: 'Browse and manage WhatsApp Business API message templates — filter by approval status, search, preview template cards with media, and add templates to your gallery',
    icon: 'collections',
    category: 'admin',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/template-gallery',
    keywords: ['template gallery', 'message templates', 'whatsapp templates', 'waba templates', 'approved templates', 'template status', 'add to gallery'],
    videoUrl: 'videos/admin/view-template-gallery.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "View All Templates" under Message Templates',
      'Filter templates by status (Approved, Pending, Rejected)',
      'Search templates by name or content',
      'Click + Add To Gallery to add approved WABA templates',
      'Preview template cards with message body, media, and buttons'
    ],
    web: {
      videoUrl: 'videos/admin/view-template-gallery.webm'
    }
  },
  {
    id: 'view_forms',
    text: 'Forms',
    tooltip: 'Create and manage custom forms — link forms to Task Types, Call Outcomes, Visit Outcomes, and Deal Stages for structured data collection during CRM activities',
    icon: 'description',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/forms',
    keywords: ['forms', 'custom forms', 'data collection', 'create form', 'link form', 'form linkages', 'task form', 'call form'],
    videoUrl: 'videos/admin/view-forms.webm',
    steps: [
      'Click CUSTOMIZE in the top bar from Dashboard',
      'Click "Link Form to Calls, Tasks" under Customize for Your Business',
      'Click + Create Form to build a new custom form',
      'View form cards with preview and linked associations',
      'Link forms to Task Types, Call Outcomes, Visit Outcomes, or Deal Stages'
    ],
    web: {
      videoUrl: 'videos/admin/view-forms.webm'
    }
  },
  // === METRICS & REPORTS ROUTES ===
  {
    id: 'view_performance_metrics',
    text: 'Performance Dashboard',
    tooltip: 'View overall team activity metrics — calls, meetings, visits, tasks — with date filters, team/user breakdown, and call outcome analysis in AG Grid tables',
    icon: 'dashboard',
    category: 'admin',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    route: '/metrics/performance',
    keywords: ['performance', 'metrics', 'kpi', 'dashboard', 'activity metrics', 'call outcomes', 'team performance', 'gatiwidhi', 'pradarshan'],
    videoUrl: 'videos/metrics/view-performance-metrics.webm',
    steps: ['Click Metrics in top navigation', 'Select Activity Metrics from contextual toolbar', 'Use date range filter to set time period', 'Select team members or view all', 'Review Activity Metrics grid with calls, meetings, visits, tasks', 'Scroll down to Call Outcome grid for detailed call analysis'],
    web: { videoUrl: 'videos/metrics/view-performance-metrics.webm' }
  },
  {
    id: 'view_calling_metrics',
    text: 'Calling Metrics',
    tooltip: 'View detailed call performance and outcome metrics — connected, not connected, busy, switched off — with team/user filters in the Call Outcome AG Grid',
    icon: 'phone_in_talk',
    category: 'admin',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    route: '/metrics/performance',
    keywords: ['call metrics', 'calling performance', 'call analytics', 'call outcomes', 'connected calls', 'busy', 'not reachable', 'call report', 'kaal report'],
    videoUrl: 'videos/metrics/view-performance-metrics.webm',
    steps: ['Click Metrics in top navigation', 'Select Activity Metrics from contextual toolbar', 'Scroll down to Call Outcome section', 'Review call outcomes: Connected, Not Connected, Busy, Switched Off, etc.', 'Use date and team filters to narrow results'],
    web: { videoUrl: 'videos/metrics/view-performance-metrics.webm' }
  },
  {
    id: 'view_custom_reports',
    text: 'Custom Reports',
    tooltip: 'Build drag-and-drop custom report dashboards with chart widgets — bar, pie, line, doughnut — using Gridstack layout for fully customizable analytics',
    icon: 'assessment',
    category: 'admin',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    route: '/metrics/custom-report',
    keywords: ['custom reports', 'report builder', 'analytics', 'charts', 'drag drop', 'gridstack', 'dashboard builder', 'custom dashboard', 'apni report'],
    videoUrl: 'videos/metrics/view-custom-reports.webm',
    steps: ['Click Metrics in top navigation', 'Select Custom Reports from contextual toolbar', 'View existing report dashboards with chart widgets', 'Drag and resize widgets on the Gridstack canvas', 'Click Add Widget to create new chart (bar, pie, line, doughnut)', 'Use filters to customize data in each widget'],
    web: { videoUrl: 'videos/metrics/view-custom-reports.webm' },
    discoverable: {
      id: 'reports-custom-builder',
      route: '/metrics',
      title: 'Build custom reports',
      description: 'Create reports with your own metrics, filters, and visualizations',
      icon: 'assessment',
      priority: 85,
      videoUrl: 'videos/metrics/view-custom-reports.webm',
      actionId: 'view_custom_reports'
    },
    onboardingRelevant: true
  },
  {
    id: 'share_report',
    text: 'Share Reports',
    tooltip: 'Generate shareable links or export reports to share insights',
    icon: 'share',
    category: 'admin',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    route: '/metrics',
    discoverable: {
      id: 'reports-share',
      route: '/metrics',
      title: 'Share reports with your team',
      description: 'Generate shareable links or export reports to share insights',
      icon: 'share',
      priority: 75
    }
  },
  {
    id: 'view_user_lead_report',
    text: 'User Lead Report',
    tooltip: 'View user-wise lead lifecycle report — see how many leads each team member has at each stage (New, Contacted, Qualified, Converted, etc.) with filterable AG Grid and export options',
    icon: 'person_search',
    category: 'admin',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    route: '/metrics/user-lead-report',
    keywords: ['user performance', 'lead report', 'conversion rates', 'lifecycle stages', 'user wise leads', 'lead status report', 'team leads', 'user lead', 'sabka lead report'],
    videoUrl: 'videos/metrics/view-user-lead-report.webm',
    steps: ['Click Metrics in top navigation', 'Select User Leads from contextual toolbar', 'View the AG Grid showing each user with lead counts per lifecycle stage', 'Click column headers to sort by any stage', 'Use filters to narrow by team or date range', 'Export data for external analysis'],
    web: { videoUrl: 'videos/metrics/view-user-lead-report.webm' }
  },
  // === LOCATION TRACKING ===
  {
    id: 'view_last_location',
    text: 'Field Team Location',
    tooltip: 'Track field team in real-time — 3-panel view with team member list, Google Maps with location pins, and activity timeline showing movement history',
    icon: 'location_on',
    category: 'admin',
    featureCategory: 'Team & Users',
    allowedRoles: MANAGER_PLUS,
    route: '/location/last-location',
    keywords: ['location', 'tracking', 'field team', 'gps', 'live location', 'map', 'team tracking', 'field visit', 'last location', 'jagah', 'sthaan'],
    videoUrl: 'videos/location/view-last-location.webm',
    steps: ['Click Live Location in top navigation', 'View the 3-panel layout: Team List, Google Map, Activity Timeline', 'Select a team member from the left panel to see their location', 'View their pin on the Google Map in the center', 'Check the right panel for activity timeline with movement history', 'Use date filter to view historical locations'],
    web: { videoUrl: 'videos/location/view-last-location.webm' }
  },
  // === DASHBOARD ROUTES ===
  {
    id: 'view_dashboard',
    text: 'Main Dashboard',
    tooltip: 'View the main dashboard with 6 key sections — KPI summary cards, activity overview charts, lead position funnel, source performance breakdown, lead aging analysis, and recent activity feed',
    icon: 'home',
    category: 'common',
    featureCategory: 'Reports & Analytics',
    allowedRoles: ALL_ROLES,
    route: '/home/dashboard',
    keywords: ['dashboard', 'home', 'overview', 'summary', 'kpi', 'charts', 'lead funnel', 'aging', 'source report', 'main page', 'mukhya prishth'],
    videoUrl: 'videos/dashboard/view-dashboard.webm',
    steps: ['Click Home or Dashboard in top navigation', 'View KPI summary cards at the top (leads, calls, conversions)', 'Review activity overview charts below KPIs', 'Check lead position funnel for pipeline visualization', 'View source performance breakdown to see which sources generate best leads', 'Review lead aging section for follow-up priorities', 'Scroll down for recent activity feed'],
    web: { videoUrl: 'videos/dashboard/view-dashboard.webm' },
    onboarding: {
      tier: 'important',
      order: 3,
      title: 'Check your dashboard',
      description: 'See KPIs, activity charts, and lead funnel at a glance',
      estimatedMinutes: 1,
      showInTrial: true,
      showInFullSetup: true
    }
  },
  {
    id: 'dashboard_customize_widgets',
    text: 'Customize Dashboard Widgets',
    tooltip: 'Add, remove, or rearrange dashboard widgets',
    icon: 'space_dashboard',
    category: 'common',
    featureCategory: 'Reports & Analytics',
    allowedRoles: ALL_ROLES,
    route: '/home/dashboard',
    discoverable: {
      id: 'dashboard-customize-widgets',
      route: '/home/dashboard',
      title: 'Customize your dashboard widgets',
      description: 'Add, remove, or rearrange widgets to see the metrics that matter most',
      icon: 'view_module',
      priority: 95
    },
    onboardingRelevant: true
  },
  {
    id: 'dashboard_filter_dates',
    text: 'Dashboard Date Filters',
    tooltip: 'Change dashboard date range filters',
    icon: 'event',
    category: 'common',
    featureCategory: 'Reports & Analytics',
    allowedRoles: ALL_ROLES,
    route: '/home/dashboard',
    discoverable: {
      id: 'dashboard-filters',
      route: '/home/dashboard',
      title: 'Filter dashboard by date range',
      description: 'View metrics for specific time periods: today, this week, this month, or custom ranges',
      icon: 'event',
      priority: 85
    },
    onboardingRelevant: true
  },
  // === ACCOUNT & PROFILE ROUTES ===
  {
    id: 'view_profile_detail',
    text: 'My Profile',
    tooltip: 'View your name, registered email (with verification status), phone number, user status, source, and account deletion option — edit name inline, change email/phone via edit icons',
    icon: 'account_circle',
    category: 'user',
    featureCategory: 'Team & Users',
    allowedRoles: ALL_ROLES,
    route: '/account/profile/(profile:profile-detail)',
    keywords: ['profile', 'personal info', 'my account', 'name', 'email', 'phone', 'verification', 'delete account', 'leave salesmax'],
    videoUrl: 'videos/account/view-profile-detail.webm',
    steps: [
      'Click your account name in the top-right corner',
      'Select "Profile & Preferences" from the dropdown',
      'View name, email, phone, status, and source',
      'Click name to edit inline — saves on blur',
      'Click edit icons to change email or phone number',
      'Use "Change Your Registered Number" button for phone updates'
    ],
    web: { videoUrl: 'videos/account/view-profile-detail.webm' }
  },
  {
    id: 'view_organization_detail',
    text: 'Organization Details',
    tooltip: 'View organization account name, business name, product/service, account type, status, and create a new workspace for another business',
    icon: 'apartment',
    category: 'user',
    featureCategory: 'Settings & Customization',
    allowedRoles: ALL_ROLES,
    route: '/account/profile/(profile:organization-detail)',
    keywords: ['organization', 'workspace info', 'company details', 'business name', 'account type', 'new organization', 'create workspace'],
    videoUrl: 'videos/account/view-organization-detail.webm',
    steps: [
      'Click your account name in the top-right corner',
      'Select "Profile & Preferences" from the dropdown',
      'Click "Organization" in the left sidebar',
      'View Account Name, Business Name, Product/Service, Type, and Status',
      'Click "Create New Organization" to set up a separate workspace'
    ],
    web: { videoUrl: 'videos/account/view-organization-detail.webm' }
  },
  {
    id: 'view_call_logging_prefs',
    text: 'Call Logging Preferences',
    tooltip: 'Configure your personal call logging and outcome preferences',
    icon: 'call',
    category: 'user',
    featureCategory: 'Settings & Customization',
    allowedRoles: ALL_ROLES,
    route: '/account/profile/(profile:preference-logging)',
    keywords: ['call preferences', 'logging settings', 'call outcomes']
  },
  {
    id: 'view_meeting_prefs',
    text: 'Meeting Preferences',
    tooltip: 'Configure your meeting and visit logging preferences',
    icon: 'event',
    category: 'user',
    featureCategory: 'Settings & Customization',
    allowedRoles: ALL_ROLES,
    route: '/account/profile/(profile:preference-meeting)',
    keywords: ['meeting preferences', 'visit settings']
  },
  {
    id: 'view_notification_prefs',
    text: 'Notification Settings',
    tooltip: 'Manage email and push notification preferences',
    icon: 'notifications',
    category: 'user',
    featureCategory: 'Settings & Customization',
    allowedRoles: ALL_ROLES,
    route: '/account/profile/(profile:preference-notification)',
    keywords: ['notifications', 'alerts', 'email preferences'],
    discoverable: {
      id: 'settings-notifications',
      route: '/admin',
      title: 'Customize your notifications',
      description: 'Choose which events trigger email, in-app, or push notifications',
      icon: 'notifications',
      priority: 85
    },
    onboardingRelevant: true
  },
  {
    id: 'view_report_prefs',
    text: 'Report Preferences',
    tooltip: 'Configure your report subscription and email delivery preferences',
    icon: 'schedule_send',
    category: 'user',
    featureCategory: 'Reports & Analytics',
    allowedRoles: ALL_ROLES,
    route: '/account/profile/(profile:preference-reports)',
    keywords: ['report preferences', 'scheduled reports', 'email reports']
  },
  {
    id: 'view_team_call_prefs',
    text: 'Team Call Settings',
    tooltip: 'Configure team-level call logging preferences',
    icon: 'groups',
    category: 'admin',
    featureCategory: 'Team & Users',
    allowedRoles: MANAGER_PLUS,
    route: '/account/teams/(team:preference-teams-logging)',
    keywords: ['team settings', 'team call preferences']
  },
  {
    id: 'view_team_meeting_prefs',
    text: 'Team Meeting Settings',
    tooltip: 'Configure team-level meeting preferences',
    icon: 'event_note',
    category: 'admin',
    featureCategory: 'Team & Users',
    allowedRoles: MANAGER_PLUS,
    route: '/account/teams/(team:preference-teams-meeting)',
    keywords: ['team meetings', 'team visit settings']
  },
  {
    id: 'view_team_notification_prefs',
    text: 'Team Notifications',
    tooltip: 'Configure team-level notification settings',
    icon: 'notifications_active',
    category: 'admin',
    featureCategory: 'Team & Users',
    allowedRoles: MANAGER_PLUS,
    route: '/account/teams/(team:preference-team-notifcations)',
    keywords: ['team notifications', 'team alerts']
  },
  {
    id: 'view_team_report_prefs',
    text: 'Team Report Settings',
    tooltip: 'Configure team report distribution and scheduling',
    icon: 'summarize',
    category: 'admin',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    route: '/account/teams/(team:preference-team-reports)',
    keywords: ['team reports', 'report distribution']
  },
  {
    id: 'manage_workspaces',
    text: 'Switch Workspace',
    tooltip: 'View all workspace memberships in a table — see account name, type, status, authorization level, and designation. Accept or reject pending invitations, search, and switch between workspaces',
    icon: 'swap_horiz',
    category: 'user',
    featureCategory: 'Settings & Customization',
    allowedRoles: ALL_ROLES,
    route: '/account/manage-account',
    keywords: ['switch workspace', 'change account', 'workspace selector', 'manage memberships', 'accept invitation', 'reject invitation', 'multiple workspaces'],
    videoUrl: 'videos/account/manage-workspaces.webm',
    steps: [
      'Click your account name in the top-right corner',
      'Select "Manage Memberships" from the dropdown',
      'View all workspace memberships with name, type, status, and role',
      'Select a row and click Accept/Reject for pending invitations',
      'Use quick search to find a workspace by name'
    ],
    web: { videoUrl: 'videos/account/manage-workspaces.webm' }
  },
  // === CONTEXTUAL INLINE ACTIONS (with step-by-step guidance) ===
  {
    id: 'assign_leads_bulk',
    text: 'Assign Leads (Bulk)',
    tooltip: 'Assign selected leads to a user, team, or distribution rule from the leads grid toolbar. Select multiple leads → click Assign → pick a user, team, or distribution rule.',
    icon: 'assignment_ind',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    route: '/leads/all-leads',
    requiresContext: true,
    workflowId: 'leads-assign',
    videoUrl: 'videos/leads/assign-leads-bulk.webm',
    keywords: ['assign leads', 'bulk assign', 'reassign', 'distribute leads', 'assign to user', 'assign to team', 'lead assignment', 'lead ko assign karo', 'lead distribute'],
    steps: [
      'Navigate to Leads page (All Leads view)',
      'Select one or more leads by clicking their checkboxes',
      'Click the "Assign" button in the bulk actions bar (or "Quick Assign" icon)',
      'Choose assignment type: User, Team, or Distribution rule',
      'Select the assignee and click "Assign" to confirm'
    ],
    web: {
      videoUrl: 'videos/leads/assign-leads-bulk.webm',
      steps: [
        'Go to Leads → All Leads',
        'Click checkboxes to select leads you want to assign',
        'Click the "Assign" button in the toolbar (appears after selection)',
        'Choose assignment type: specific User, Team, or Distribution rule',
        'Select the target and click "Assign" to confirm'
      ]
    }
  },
  {
    id: 'delete_leads_bulk',
    text: 'Delete Leads',
    tooltip: 'Delete selected leads from the system. Select leads on the grid → click Delete → confirm in the popup. This action cannot be undone.',
    icon: 'delete',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    route: '/leads/all-leads',
    requiresContext: true,
    workflowId: 'leads-delete',
    videoUrl: 'videos/leads/delete-leads-bulk.webm',
    keywords: ['delete leads', 'remove leads', 'bulk delete', 'delete contacts', 'remove contacts', 'hatao', 'delete karo', 'lead delete karo'],
    steps: [
      'Navigate to Leads page',
      'Select leads to delete using checkboxes',
      'Click the "Delete" button in the toolbar',
      'Confirm deletion in the popup dialog'
    ],
    web: {
      videoUrl: 'videos/leads/delete-leads-bulk.webm',
      steps: [
        'Go to Leads → All Leads',
        'Select one or more leads using checkboxes',
        'Click the red "Delete" button in the toolbar',
        'Confirm deletion in the warning popup — this cannot be undone'
      ]
    }
  },
  {
    id: 'export_leads_filtered',
    text: 'Export Leads',
    tooltip: 'Export selected or filtered leads to CSV or Excel. Select leads on the grid → click Export → choose format → download.',
    icon: 'download',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    route: '/leads/all-leads',
    requiresContext: true,
    workflowId: 'leads-export',
    videoUrl: 'videos/leads/export-leads-filtered.webm',
    keywords: ['export leads', 'download leads', 'export contacts', 'csv', 'excel', 'export data', 'download data', 'leads download karo'],
    steps: [
      'Navigate to Leads page',
      'Apply filters if needed (optional)',
      'Select leads using checkboxes OR click "Select All"',
      'Click the "Export" dropdown button',
      'Choose format (CSV or Excel)',
      'Click "Export" to download'
    ],
    web: {
      videoUrl: 'videos/leads/export-leads-filtered.webm',
      steps: [
        'Go to Leads → All Leads',
        'Apply filters if needed (optional)',
        'Select leads using checkboxes or "Select All"',
        'Click the "Export" button in the toolbar',
        'Choose format — CSV or Excel',
        'Click "Export" to download the file'
      ]
    }
  },
  {
    id: 'add_tag_to_leads',
    text: 'Add Tags to Leads',
    tooltip: 'Tag selected leads for categorization and filtering. Select leads → click Tag button → pick an existing tag or create a new one.',
    icon: 'label',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    workflowId: 'add-tag-to-leads',
    videoUrl: 'videos/leads/add-tag-to-leads.webm',
    keywords: ['add tag', 'tag leads', 'label leads', 'categorize', 'bulk tag', 'tag lagao', 'lead tag'],
    steps: [
      'Navigate to Leads page',
      'Select one or more leads using checkboxes',
      'Click the "Add Tag" dropdown button',
      'Select an existing tag OR click "+ New Tag" to create one',
      'Confirm the action'
    ],
    web: {
      videoUrl: 'videos/leads/add-tag-to-leads.webm',
      steps: [
        'Go to Leads → All Leads',
        'Select one or more leads using checkboxes',
        'Click the "Tag" button in the toolbar',
        'Pick an existing tag or click "+ New Tag" to create one',
        'Tag is applied to all selected leads'
      ]
    }
  },
  {
    id: 'change_lead_lifecycle',
    text: 'Change Lead Lifecycle',
    tooltip: 'Change the lifecycle stage of selected leads (e.g., Prospecting → Qualified → Opportunity). Select leads → click Change Category → pick the new stage.',
    icon: 'update',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    videoUrl: 'videos/leads/change-lead-lifecycle.webm',
    keywords: ['change lifecycle', 'lead stage', 'lifecycle stage', 'move to stage', 'update status', 'category change', 'lead category badlo'],
    steps: [
      'Navigate to Leads page',
      'Select leads using checkboxes',
      'Click "Change Category" button',
      'Select the new lifecycle stage',
      'Confirm the change'
    ],
    web: {
      videoUrl: 'videos/leads/change-lead-lifecycle.webm',
      steps: [
        'Go to Leads → All Leads',
        'Select leads using checkboxes',
        'Click "Change Category" in the toolbar',
        'Select the new lifecycle stage from the dialog',
        'Confirm — all selected leads move to the new stage'
      ]
    }
  },
  {
    id: 'edit_single_lead',
    text: 'Edit Lead Details',
    tooltip: 'Edit details of a specific lead — name, phone, email, custom fields. Select one lead → click Edit → modify details in the sidebar → Save.',
    icon: 'edit',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    workflowId: 'edit-single-lead',
    videoUrl: 'videos/leads/edit-single-lead.webm',
    keywords: ['edit lead', 'update lead', 'modify contact', 'change lead details', 'lead edit karo', 'lead update'],
    steps: [
      'Navigate to Leads page',
      'Select exactly ONE lead using checkbox',
      'Click the "Edit" button that appears',
      'Modify the lead details in the sidebar',
      'Click "Save" to update'
    ],
    web: {
      videoUrl: 'videos/leads/edit-single-lead.webm',
      steps: [
        'Go to Leads → All Leads',
        'Select exactly one lead using its checkbox',
        'Click the "Edit" (pencil) button in the toolbar',
        'Modify lead details in the sidebar form — name, phone, email, custom fields',
        'Click "Save" to update the lead'
      ]
    }
  },
  {
    id: 'call_lead_from_grid',
    text: 'Call a Lead',
    tooltip: 'Place a call to a lead directly from the leads grid. Hover over a lead row → phone icon appears → click to call. Requires cloud telephony or SIM calling.',
    icon: 'phone',
    category: 'user',
    featureCategory: 'Communication & Calling',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    videoUrl: 'videos/leads/call-lead-from-grid.webm',
    keywords: ['call lead', 'phone call', 'dial', 'make call', 'place call', 'lead ko call karo', 'call karo'],
    steps: [
      'Navigate to Leads page',
      'Find the lead in the grid',
      'Hover over the lead row — phone and WhatsApp icons appear',
      'Click the phone icon to initiate a call',
      'If multiple numbers exist, select which number to call'
    ],
    web: {
      videoUrl: 'videos/leads/call-lead-from-grid.webm',
      steps: [
        'Go to Leads → All Leads',
        'Find the lead you want to call',
        'Hover over the lead row — action icons appear on hover',
        'Click the phone icon to initiate a call',
        'If the lead has multiple numbers, a picker appears to choose'
      ]
    }
  },
  {
    id: 'start_whatsapp_lead',
    text: 'Start WhatsApp Chat',
    tooltip: 'Start a WhatsApp conversation with a lead directly from the grid. Hover over a lead row → WhatsApp icon appears → click to open chat sidebar. Requires WhatsApp Business API.',
    icon: 'chat',
    category: 'user',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    videoUrl: 'videos/leads/start-whatsapp-lead.webm',
    keywords: ['whatsapp', 'chat', 'message', 'start conversation', 'whatsapp lead', 'whatsapp bhejo', 'whatsapp message'],
    steps: [
      'Navigate to Leads page',
      'Find the lead in the grid',
      'Hover over the lead row — WhatsApp icon appears',
      'Click the WhatsApp icon to open the chat sidebar',
      'Conversation window opens with the lead'
    ],
    web: {
      videoUrl: 'videos/leads/start-whatsapp-lead.webm',
      steps: [
        'Go to Leads → All Leads',
        'Find the lead you want to message',
        'Hover over the lead row — action icons appear on hover',
        'Click the WhatsApp icon to open the chat sidebar',
        'Conversation opens — type your message and send'
      ]
    }
  },
  {
    id: 'create_deal_from_lead',
    text: 'Create Deal from Lead',
    tooltip: 'Convert a lead to an opportunity/deal',
    icon: 'monetization_on',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    // videoUrl: pending re-record
    workflowId: 'leads-edit-multiple-deals',
    keywords: ['create deal', 'convert to opportunity', 'make deal', 'lead to deal'],
    steps: [
      'Navigate to Leads page',
      'Select ONE lead using checkbox',
      'Click "Create Deal" button (visible only in manual deal mode)',
      'Fill in deal details in the dialog',
      'Click "Save" to create the deal'
    ],
    onboarding: {
      tier: 'important',
      order: 6,
      title: 'Create your first deal',
      description: 'Convert a lead into a deal to track it through your sales pipeline',
      completionKey: 'account.totalDeals',
      completionCheck: 'count',
      completionThreshold: 1,
      estimatedMinutes: 2,
      showInTrial: true,
      showInFullSetup: true
    }
  },
  // log_call_for_lead — merged into log_call (same LogCallComponent, same video)
  {
    id: 'create_task_for_lead',
    text: 'Create Task for Lead',
    tooltip: 'Create a follow-up task for selected lead',
    icon: 'add_task',
    category: 'user',
    featureCategory: 'Tasks & Follow-ups',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    keywords: ['create task', 'add task', 'follow up', 'reminder', 'schedule task'],
    steps: [
      'Navigate to Leads page',
      'Select ONE lead using checkbox',
      'Click "Create Task" button',
      'Fill in task details (type, date, notes)',
      'Click "Save"'
    ]
  },
  {
    id: 'add_leads_to_list',
    text: 'Add Leads to List',
    tooltip: 'Add selected leads to a contact list/segment',
    icon: 'playlist_add',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    keywords: ['add to list', 'add to segment', 'create list', 'contact list'],
    steps: [
      'Navigate to Leads page',
      'Select leads using checkboxes',
      'Click "Add to List" button',
      'Select existing list OR create new list',
      'Confirm'
    ]
  },
  {
    id: 'send_whatsapp_broadcast',
    text: 'Send WhatsApp Broadcast',
    tooltip: 'Send bulk WhatsApp message to selected leads',
    icon: 'campaign',
    category: 'user',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: MANAGER_PLUS,
    route: '/leads/all-leads',
    requiresContext: true,
    workflowId: 'leads-start-whatsapp',
    keywords: ['whatsapp broadcast', 'bulk whatsapp', 'send message', 'campaign', 'waba'],
    steps: [
      'Navigate to Leads page',
      'Select leads using checkboxes',
      'Click the "Campaign" button in the bulk actions bar',
      'Choose a WhatsApp template and customize message variables',
      'Preview and click "Send" to broadcast'
    ]
  },
  {
    id: 'rechurn_leads',
    text: 'Rechurn Leads',
    tooltip: 'Rechurn selected leads to make them active again',
    icon: 'refresh',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    videoUrl: 'videos/leads/rechurn-leads.webm',
    web: { videoUrl: 'videos/leads/rechurn-leads.webm' },
    keywords: ['rechurn', 'reactivate leads', 'make active', 'reopen leads'],
    steps: [
      'Navigate to Leads page',
      'Select leads using checkboxes',
      'Click "Rechurn" button in toolbar',
      'Confirm the action'
    ]
  },
  {
    id: 'filter_leads_advanced',
    text: 'Apply Advanced Filters',
    tooltip: 'Filter leads using advanced conditions',
    icon: 'filter_list',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    videoUrl: 'videos/leads/filter-leads-advanced.webm',
    web: { videoUrl: 'videos/leads/filter-leads-advanced.webm' },
    workflowId: 'leads-filter',
    keywords: ['filter', 'search', 'advanced filter', 'find leads', 'query'],
    steps: [
      'Navigate to Leads page',
      'Use the Smart View dropdown for quick filters (Fresh, Unassigned, etc.)',
      'Or hover on a column header and click the filter funnel icon',
      'Set filter conditions (contains, equals, date range, etc.)',
      'Optionally save the filtered view as a Smart View for reuse'
    ],
    onboarding: {
      tier: 'helpful',
      order: 2,
      title: 'Filter your leads',
      description: 'Use advanced filters to find exactly the leads you need',
      estimatedMinutes: 2,
      showInTrial: true,
      showInFullSetup: true
    }
  },
  {
    id: 'hide_show_columns',
    text: 'Customize Grid Columns',
    tooltip: 'Show or hide columns in the leads grid',
    icon: 'view_column',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    videoUrl: 'videos/leads/hide-show-columns.webm',
    web: { videoUrl: 'videos/leads/hide-show-columns.webm' },
    workflowId: 'leads-hide-columns',
    keywords: ['hide columns', 'show columns', 'customize grid', 'column visibility', 'table columns'],
    steps: [
      'Navigate to Leads page',
      'Click the "Customize Table Columns" icon in the toolbar',
      'Search for a column or scroll the list',
      'Toggle visibility with the eye icon, drag to reorder',
      'Click "Save View" to preserve your layout'
    ]
  },
  {
    id: 'create_smart_view',
    text: 'Create Smart View',
    tooltip: 'Save current filters as a reusable Smart View',
    icon: 'bookmark',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    videoUrl: 'videos/leads/create-smart-view.webm',
    web: { videoUrl: 'videos/leads/create-smart-view.webm' },
    workflowId: 'leads-smart-views',
    keywords: ['smart view', 'save filter', 'saved view', 'create view'],
    steps: [
      'Navigate to Leads page',
      'Apply filters, column arrangement, or sorts you want to preserve',
      'Click the "Save View" link in the toolbar',
      'Choose "Save as New Smart View" from the menu',
      'Name the view and save — switch between views anytime from the dropdown'
    ],
    discoverable: {
      id: 'smart-view-save',
      route: '/leads/all-leads',
      title: 'Save this as a Smart View',
      description: 'Save your filters and column settings for quick access later',
      icon: 'bookmark',
      priority: 85,
      videoUrl: 'videos/leads/create-smart-view.webm'
    },
    onboardingRelevant: true,
    onboarding: {
      tier: 'helpful',
      order: 3,
      title: 'Save a Smart View',
      description: 'Save your favorite filters for one-click access',
      estimatedMinutes: 1,
      showInTrial: true,
      showInFullSetup: true
    }
  },
  // create_dashboard_metric removed — no video/workflow/steps, hollow entry
  {
    id: 'bulk_select_leads',
    text: 'Bulk Select Leads',
    tooltip: 'Select multiple leads to run assignments, WhatsApp, or tagging together',
    icon: 'select_all',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    videoUrl: 'videos/leads/bulk-select-leads.webm',
    web: { videoUrl: 'videos/leads/bulk-select-leads.webm' },
    workflowId: 'leads-select-all-actions',
    discoverable: {
      id: 'bulk-actions',
      route: '/leads/all-leads',
      title: 'Select multiple contacts for bulk actions',
      description: 'Assign, tag, or message multiple contacts at once',
      icon: 'select_all',
      priority: 75,
      videoUrl: 'videos/leads/bulk-select-leads.webm',
      targetSelector: '.row-checkbox'
    },
    onboardingRelevant: true
  },
  // lead_keyboard_shortcuts removed — J/K shortcuts not implemented
  {
    id: 'bulk_upload_leads_guided',
    text: 'Bulk Upload Leads (Guided)',
    tooltip: 'Upload leads from Excel/CSV file with step-by-step guidance',
    icon: 'upload_file',
    category: 'admin',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ADMIN_ONLY,
    route: '/leads/bulk-add-new',
    requiresContext: true,
    workflowId: 'bulk-upload-leads',
    keywords: ['bulk upload', 'import leads', 'csv upload', 'excel import', 'upload file'],
    steps: [
      'Navigate to Bulk Upload page',
      'Enter list name and select source',
      'Upload your CSV/Excel file',
      'Map file columns to system fields',
      'Optionally assign leads to users/teams',
      'Click "Complete Upload" to import'
    ],
    discoverable: {
      id: 'bulk-upload-guide',
      route: '/leads/bulk-add-new',
      title: 'Bulk upload leads faster',
      description: 'Let AI guide you through the wizard. Use the bundled sample CSV to try it safely.',
      icon: 'upload_file',
      priority: 95,
      workflowId: 'bulk-upload-leads'
    },
    onboardingRelevant: true,
    onboarding: {
      tier: 'important',
      order: 5,
      title: 'Import leads in bulk',
      description: 'Upload your existing contacts from a CSV or Excel file',
      estimatedMinutes: 3,
      adminOnly: true,
      showInTrial: true,
      showInFullSetup: true
    }
  },
  {
    id: 'assign_tasks_bulk',
    text: 'Assign Tasks (Bulk)',
    tooltip: 'Reassign selected tasks to different users',
    icon: 'assignment_ind',
    category: 'user',
    featureCategory: 'Tasks & Follow-ups',
    allowedRoles: MANAGER_PLUS,
    route: '/todo/grid',
    requiresContext: true,
    videoUrl: 'videos/tasks/assign-tasks-bulk.webm',
    web: { videoUrl: 'videos/tasks/assign-tasks-bulk.webm' },
    keywords: ['assign tasks', 'reassign tasks', 'task assignment', 'delegate tasks'],
    steps: [
      'Navigate to Tasks page',
      'Select tasks using checkboxes',
      'Click "Assign Tasks" button',
      'Select user to assign to',
      'Click "Assign"'
    ]
  },
  {
    id: 'complete_task',
    text: 'Complete Task',
    tooltip: 'Mark a task as completed',
    icon: 'check_circle',
    category: 'user',
    featureCategory: 'Tasks & Follow-ups',
    allowedRoles: ALL_ROLES,
    route: '/todo/grid',
    requiresContext: true,
    videoUrl: 'videos/tasks/complete-task.webm',
    web: { videoUrl: 'videos/tasks/complete-task.webm' },
    keywords: ['complete task', 'mark done', 'finish task', 'close task'],
    steps: [
      'Navigate to Tasks page',
      'Select ONE task using checkbox',
      'Click "Complete Task" button',
      'Fill in outcome/notes if required',
      'Click "Save"'
    ]
  },
  // edit_task — removed (not useful enough for video demo)
  {
    id: 'view_deal_history',
    text: 'View Deal Payment History',
    tooltip: 'View payment history for a specific deal',
    icon: 'history',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/opportunity-table',
    requiresContext: true,
    videoUrl: 'videos/deals/view-deal-history.webm',
    web: { videoUrl: 'videos/deals/view-deal-history.webm' },
    keywords: ['payment history', 'deal history', 'view payments', 'transaction history'],
    steps: [
      'Navigate to Deals page (Table view)',
      'Select ONE deal using checkbox',
      'Click "View History" button',
      'Payment history will be displayed'
    ]
  },
  // === ADDITIONAL ACTIONS FROM SUPPORT DOCS ===
  {
    id: 'close_lead_manual',
    text: 'Close Lead Manually',
    tooltip: 'Manually close a lead by marking it as won, lost, or not interested',
    icon: 'cancel',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    videoUrl: 'videos/leads/close-lead-manual.webm',
    web: { videoUrl: 'videos/leads/close-lead-manual.webm' },
    workflowId: 'leads-manual-close',
    keywords: ['close lead', 'mark won', 'mark lost', 'not interested', 'manual close', 'won', 'lost', 'close karo'],
    steps: [
      'Navigate to Leads page',
      'Select ONE or more leads using checkboxes',
      'Click "Change Category" or lifecycle button',
      'Select close reason (Won/Lost/Not Interested)',
      'Add notes if required',
      'Confirm closure'
    ]
  },
  {
    id: 'setup_call_recording',
    text: 'Set Up Call Recording',
    tooltip: 'Configure automatic call recording settings for your workspace',
    icon: 'settings_voice',
    category: 'admin',
    featureCategory: 'Communication & Calling',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings',
    keywords: ['call recording', 'recording setup', 'enable recording', 'call settings', 'record calls', 'automatic recording', 'recording kaise kare', 'enable call recorder'],
    steps: [
      'Navigate to Admin > Settings',
      'Find Call/Telephony settings section',
      'Enable "Automatic Call Recording" toggle',
      'Configure storage and retention settings if needed',
      'Click "Save" to apply changes'
    ]
  },
  {
    id: 'view_broadcast_results',
    text: 'View Broadcast Results',
    tooltip: 'View analytics and results of WhatsApp broadcast campaigns',
    icon: 'analytics',
    category: 'user',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: MANAGER_PLUS,
    route: '/conversations/broadcast',
    keywords: ['broadcast results', 'campaign analytics', 'whatsapp stats', 'broadcast performance', 'campaign metrics', 'delivery report'],
    steps: [
      'Navigate to Conversations > Broadcasts',
      'Find your campaign in the list',
      'Click on the campaign to view details',
      'Review sent, delivered, read, and replied statistics'
    ]
  },
  {
    id: 'setup_auto_reply',
    text: 'Set Up Auto Reply',
    tooltip: 'Configure automated reply messages for WhatsApp conversations',
    icon: 'auto_mode',
    category: 'admin',
    featureCategory: 'Messaging & WhatsApp',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/conversations/reply',
    keywords: ['auto reply', 'automated messages', 'whatsapp auto response', 'chatbot', 'automatic reply', 'auto responder'],
    steps: [
      'Navigate to Conversations > Auto Reply',
      'Click "Create New Auto Reply"',
      'Set trigger conditions (keywords, time, etc.)',
      'Write the automated response message',
      'Save and activate'
    ]
  },
  {
    id: 'manage_personal_calls',
    text: 'Manage Personal Call Tracking',
    tooltip: 'Configure which personal numbers should or should not be tracked',
    icon: 'phone_disabled',
    category: 'user',
    featureCategory: 'Settings & Customization',
    allowedRoles: ALL_ROLES,
    route: '/account/profile/(profile:preference-logging)',
    keywords: ['personal calls', 'do not track', 'exclude numbers', 'personal numbers', 'call privacy', 'untrack calls', 'private numbers'],
    steps: [
      'Navigate to Profile > Call Logging Preferences',
      'Find "Personal Numbers" or "Do Not Track" section',
      'Add phone numbers to exclude from tracking',
      'Save preferences'
    ]
  },
  {
    id: 'setup_platform_integrations',
    text: 'Set Up Platform Integrations',
    tooltip: 'Connect external platforms like Facebook, Google Ads, Indiamart, Justdial, Tradeindia, Razorpay for lead capture and automation',
    icon: 'integration_instructions',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/integrations',
    keywords: ['integrations', 'facebook leads', 'google adwords', 'indiamart', 'justdial', 'tradeindia', 'razorpay', 'platform integration', 'lead source', 'fb leads', 'google ads'],
    steps: [
      'Navigate to Admin > Integrations',
      'Find the platform you want to integrate',
      'Click "Connect" or "Set Up"',
      'Follow authentication and configuration steps',
      'Map fields and enable auto-import if needed',
      'Test and activate integration'
    ]
  },
  {
    id: 'integrate_indiamart',
    text: 'Integrate Indiamart',
    tooltip: 'Connect Indiamart lead source to Salesmax',
    icon: 'storefront',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/integrations/(integration:crm)',
    workflowId: 'integrate-indiamart',
    keywords: ['indiamart integration', 'import indiamart leads'],
    steps: [
      'Go to Admin > Integrations > CRM/Lead Sources',
      'Select Indiamart connector',
      'Enter Indiamart credentials/API key',
      'Test connection and activate'
    ]
  },
  {
    id: 'integrate_tradeindia',
    text: 'Integrate Tradeindia',
    tooltip: 'Connect Tradeindia to automatically capture leads',
    icon: 'store',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/integrations/(integration:crm)',
    keywords: ['tradeindia integration', 'trade india leads'],
    steps: [
      'Navigate to Admin > Integrations',
      'Choose Tradeindia connector',
      'Provide API credentials',
      'Map fields if required and activate'
    ]
  },
  {
    id: 'integrate_facebook',
    text: 'Integrate Facebook Leads',
    tooltip: 'Connect Facebook Lead Ads to capture leads automatically',
    icon: 'facebook',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/integrations/(integration:crm)',
    workflowId: 'integrate-facebook',
    keywords: ['facebook integration', 'facebook leads', 'meta leads'],
    steps: [
      'Go to Admin > Integrations > Facebook',
      'Log in with Facebook business account',
      'Select pages/forms to sync',
      'Allow permissions and save'
    ]
  },
  {
    id: 'setup_cloud_telephony',
    text: 'Integrate Cloud Telephony/IVR',
    tooltip: 'Connect cloud telephony or IVR system for advanced calling features',
    icon: 'cloud_queue',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/integrations',
    workflowId: 'setup-cloud-telephony',
    keywords: ['cloud telephony', 'ivr', 'cloud calling', 'telephony integration', 'call center', 'phone system'],
    steps: [
      'Navigate to Admin > Integrations',
      'Find Cloud Telephony section',
      'Enter API credentials from your provider',
      'Configure call routing and IVR settings',
      'Test connection',
      'Activate for users'
    ]
  },
  // === DEALS & OPPORTUNITIES INLINE ACTIONS ===
  {
    id: 'edit_deal',
    text: 'Edit Deal/Opportunity',
    tooltip: 'Edit details of a selected deal or opportunity',
    icon: 'edit',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/opportunity-table',
    requiresContext: true,
    videoUrl: 'videos/deals/edit-deal.webm',
    web: { videoUrl: 'videos/deals/edit-deal.webm' },
    keywords: ['edit deal', 'update deal', 'modify opportunity', 'change deal details', 'edit opportunity', 'deal amount', 'update value', 'change deal size'],
    steps: [
      'Navigate to Deals page (Table or Kanban view)',
      'Select ONE deal using checkbox or click on deal card',
      'Click "Edit" button or click on the deal to open details',
      'Modify deal details in the sidebar/dialog',
      'Click "Save" to update'
    ]
  },
  {
    id: 'update_deal_stage',
    text: 'Update Deal Stage',
    tooltip: 'Move deal to a different stage in the sales pipeline',
    icon: 'trending_up',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/opportunity-list',
    requiresContext: true,
    videoUrl: 'videos/deals/update-deal-stage.webm',
    web: { videoUrl: 'videos/deals/update-deal-stage.webm' },
    keywords: ['move deal', 'change stage', 'deal stage', 'pipeline', 'move to stage', 'update status'],
    steps: [
      'Navigate to Deals Kanban view',
      'Find the deal card',
      'Drag and drop the card to the desired stage column',
      'Or select deal and use "Update Stage" button',
      'Confirm the stage change'
    ]
  },
  {
    id: 'export_deals',
    text: 'Export Deals',
    tooltip: 'Export selected or filtered deals to CSV/Excel',
    icon: 'download',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: MANAGER_PLUS,
    route: '/opportunities/opportunity-table',
    requiresContext: true,
    videoUrl: 'videos/deals/export-deals.webm',
    web: { videoUrl: 'videos/deals/export-deals.webm' },
    keywords: ['export deals', 'download deals', 'export opportunities', 'csv', 'excel', 'export data'],
    steps: [
      'Navigate to Deals page (Table view)',
      'Apply filters if needed (optional)',
      'Select deals using checkboxes OR click "Select All"',
      'Click the "Export" dropdown button',
      'Choose format (CSV or Excel)',
      'Click "Export" to download'
    ]
  },
  // assign_deals removed — no Assign button exists on deals table toolbar
  // delete_deals removed — no Delete button exists on deals table toolbar
  // === QUOTATIONS INLINE ACTIONS ===
  {
    id: 'create_quotation',
    text: 'Create Quotation',
    tooltip: 'Build professional quotations with a 3-panel builder — drag products from the catalog, edit quantities & pricing, apply discounts/tax, and optionally attach a Razorpay payment link. Found under Opportunities > Quotes',
    icon: 'request_quote',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/quotes',
    requiresContext: true,
    workflowId: 'create-quotation',
    keywords: ['create quote', 'new quotation', 'quotation', 'proposal', 'estimate', 'quote builder', 'price quote', 'proforma', 'invoice', 'billing', 'payment link', 'razorpay', 'drag drop products'],
    videoUrl: 'videos/quotes/create-quotation.webm',
    steps: [
      'Go to Opportunities > Quotes from the top navigation',
      'Click "+ New Quote" to open the 3-panel quotation builder',
      'Fill quote details on the left — quote number, customer, dates, subject, terms',
      'Drag products from the right catalog panel into the center line-items area',
      'Edit quantity, rate, and tax slab directly in the line items table',
      'Review subtotal, apply discount (% or ₹), set TDS/TCS — totals auto-calculate',
      'Toggle "Attach Payment Link" if Razorpay is integrated to generate a payment link',
      'Click "Save as Draft" to continue later, or "Save & Download PDF" to finalize'
    ],
    web: { videoUrl: 'videos/quotes/create-quotation.webm' }
  },
  {
    id: 'edit_quotation',
    text: 'Edit Quotation',
    tooltip: 'Open and modify an existing quotation — update products, pricing, customer details, or terms. Select a DRAFT or SENT quote from the list and click Edit to reopen the builder',
    icon: 'edit',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/quotes',
    requiresContext: true,
    keywords: ['edit quote', 'update quotation', 'modify quote', 'change quotation', 'edit draft', 'revise quote', 'amend quotation'],
    videoUrl: 'videos/quotes/edit-quotation.webm',
    steps: [
      'Go to Opportunities > Quotes from the top navigation',
      'Click on a quote row to select it (must be DRAFT or SENT status)',
      'Click the "Edit" button in the toolbar',
      'The 3-panel Quotation Builder opens with all existing data pre-filled',
      'Modify line items, pricing, discount, customer, or terms as needed',
      'Click "Save as Draft" or "Save & Download PDF" to update'
    ],
    web: { videoUrl: 'videos/quotes/edit-quotation.webm' }
  },
  {
    id: 'send_quotation',
    text: 'Send Quotation',
    tooltip: 'Send quotation to customer via email or WhatsApp',
    icon: 'send',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/quotes',
    requiresContext: true,
    workflowId: 'send-quotation',
    videoUrl: 'videos/quotes/send-quotation.webm',
    web: { videoUrl: 'videos/quotes/send-quotation.webm' },
    keywords: ['send quote', 'email quotation', 'share quote', 'send proposal'],
    steps: [
      'Navigate to Quotations page',
      'Select the quotation',
      'Click "Send" or share button',
      'Choose delivery method (Email/WhatsApp)',
      'Add message if needed',
      'Click "Send" to deliver'
    ]
  },
  {
    id: 'export_quotes',
    text: 'Export Quotations',
    tooltip: 'Export selected quotations to CSV or Excel — select one or more quotes, click Export, choose format, and download for reporting',
    icon: 'download',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: MANAGER_PLUS,
    route: '/opportunities/quotes',
    requiresContext: true,
    keywords: ['export quotes', 'download quotations', 'csv', 'excel', 'export data', 'quote report', 'bulk export'],
    videoUrl: 'videos/quotes/export-quotes.webm',
    steps: [
      'Go to Opportunities > Quotes from the top navigation',
      'Select one or more quotes by clicking on rows (or use "Select All")',
      'Click the "Export" button that appears in the toolbar',
      'Choose export format — CSV or Excel',
      'Click "Export" to download the file'
    ],
    web: { videoUrl: 'videos/quotes/export-quotes.webm' }
  },
  // === TASKS ADDITIONAL ACTIONS ===
  {
    id: 'update_task_status',
    text: 'Update Task Status',
    tooltip: 'Update the status of a task without completing it',
    icon: 'update',
    category: 'user',
    featureCategory: 'Tasks & Follow-ups',
    allowedRoles: ALL_ROLES,
    route: '/todo/grid',
    requiresContext: true,
    videoUrl: 'videos/tasks/complete-task.webm',
    web: { videoUrl: 'videos/tasks/complete-task.webm' },
    keywords: ['update status', 'change task status', 'task progress', 'update task'],
    steps: [
      'Navigate to Tasks page',
      'Select ONE task using checkbox',
      'Click "Update Status" button',
      'Select new status or add progress notes',
      'Click "Save"'
    ]
  },
  // delete_tasks — removed (tasks can't be deleted as they are linked to deals)
  {
    id: 'export_tasks',
    text: 'Export Tasks',
    tooltip: 'Export tasks list to CSV/Excel',
    icon: 'download',
    category: 'user',
    featureCategory: 'Tasks & Follow-ups',
    allowedRoles: MANAGER_PLUS,
    route: '/todo/grid',
    requiresContext: true,
    videoUrl: 'videos/tasks/export-tasks.webm',
    web: { videoUrl: 'videos/tasks/export-tasks.webm' },
    keywords: ['export tasks', 'download tasks', 'task list export'],
    steps: [
      'Navigate to Tasks page',
      'Apply filters if needed',
      'Select tasks OR click "Select All"',
      'Click "Export" button',
      'Choose format and download'
    ]
  },
  // === PAYMENTS INLINE ACTIONS ===
  {
    id: 'export_payments',
    text: 'Export Payments',
    tooltip: 'Export payment data from the Payments table to CSV or Excel. Navigate to Opportunities → Payments, filter by status if needed, select rows, click Export, choose format, and download.',
    icon: 'download',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: MANAGER_PLUS,
    route: '/opportunities/payments/table',
    requiresContext: true,
    keywords: ['export payments', 'download payments', 'payment report', 'payment csv', 'payment excel', 'payment download'],
    videoUrl: 'videos/payments/export-payments.webm',
    steps: [
      'Navigate to Opportunities → Payments (table view)',
      'Filter by status if needed (Pending / Paid / Cancelled)',
      'Select payment rows or use "Select All"',
      'Click the "Export" button in the toolbar',
      'Choose format (CSV/Excel) and click Export to download'
    ],
    web: {
      videoUrl: 'videos/payments/export-payments.webm',
    }
  },
  // === ACTIVITIES & ENGAGEMENTS ===
  {
    id: 'export_activities',
    text: 'Export Activities',
    tooltip: 'Export activity log to CSV/Excel',
    icon: 'download',
    category: 'user',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    route: '/activities/activities',
    requiresContext: true,
    keywords: ['export activities', 'download activities', 'activity log export', 'engagement export'],
    steps: [
      'Navigate to Activities page',
      'Apply filters if needed',
      'Click "Export" button',
      'Choose format and download'
    ]
  },
  {
    id: 'filter_activities',
    text: 'Filter Activities',
    tooltip: 'Apply filters to find specific activities',
    icon: 'filter_list',
    category: 'user',
    featureCategory: 'Reports & Analytics',
    allowedRoles: ALL_ROLES,
    route: '/activities/activities',
    requiresContext: true,
    keywords: ['filter activities', 'search activities', 'activity filter'],
    steps: [
      'Navigate to Activities page',
      'Click filter icon/button',
      'Select filter criteria (date, type, user, etc.)',
      'Click "Apply" to filter results'
    ]
  },
  // === QUOTATIONS ADDITIONAL ACTIONS ===
  {
    id: 'view_quote_pdf',
    text: 'View Quote PDF',
    tooltip: 'Preview or download the PDF version of a finalized quotation — select a non-draft quote and click the PDF icon in the toolbar',
    icon: 'picture_as_pdf',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/quotes',
    requiresContext: true,
    keywords: ['view pdf', 'quote pdf', 'quotation pdf', 'download pdf', 'print quote', 'preview quote', 'share pdf'],
    videoUrl: 'videos/quotes/view-quote-pdf.webm',
    steps: [
      'Go to Opportunities > Quotes from the top navigation',
      'Filter by "Sent" status to see finalized quotes',
      'Click on a quote row to select it (must NOT be DRAFT)',
      'Click the "PDF" button in the toolbar',
      'The PDF opens in a new tab — download or print from there'
    ],
    web: { videoUrl: 'videos/quotes/view-quote-pdf.webm' }
  },
  {
    id: 'mark_quote_paid',
    text: 'Mark Quote as Paid',
    tooltip: 'Update a sent quotation status to "Paid" when payment is received — select a SENT quote and click "Mark Paid" in the toolbar',
    icon: 'attach_money',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/quotes',
    requiresContext: true,
    keywords: ['mark paid', 'quote paid', 'payment received', 'paid invoice', 'confirm payment', 'close quote'],
    videoUrl: 'videos/quotes/mark-quote-paid.webm',
    steps: [
      'Go to Opportunities > Quotes from the top navigation',
      'Filter by "Sent" status to see active quotes',
      'Click on a SENT quote row to select it',
      'Click "Mark Paid" in the toolbar',
      'Quote status updates from SENT → PAID (irreversible)'
    ],
    web: { videoUrl: 'videos/quotes/mark-quote-paid.webm' }
  },
  {
    id: 'mark_quote_cancelled',
    text: 'Mark Quote as Cancelled',
    tooltip: 'Cancel a sent quotation — marks the quote as void so it is no longer active. Select a SENT quote and click "Mark Cancelled"',
    icon: 'cancel',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/quotes',
    requiresContext: true,
    keywords: ['mark cancelled', 'cancel quote', 'cancel quotation', 'reject quote', 'void quote', 'revoke quotation'],
    videoUrl: 'videos/quotes/mark-quote-cancelled.webm',
    steps: [
      'Go to Opportunities > Quotes from the top navigation',
      'Filter by "Sent" status to see active quotes',
      'Click on a SENT quote row to select it',
      'Click "Mark Cancelled" in the toolbar',
      'Quote status updates from SENT → CANCELLED (quote stays in list for records)'
    ],
    web: { videoUrl: 'videos/quotes/mark-quote-cancelled.webm' }
  },
  {
    id: 'update_quote_payment',
    text: 'Update Quote Payment',
    tooltip: 'Record or update payment details for a quotation with offline payment — track partial payments and payment status changes',
    icon: 'payment',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/quotes',
    requiresContext: true,
    keywords: ['update payment', 'payment details', 'payment status', 'partial payment', 'offline payment', 'record payment', 'payment tracking'],
    videoUrl: 'videos/quotes/update-quote-payment.webm',
    steps: [
      'Go to Opportunities > Quotes from the top navigation',
      'Filter by "Sent" status to see quotes awaiting payment',
      'Click on a SENT quote with offline payment (status: Created or Partial Paid)',
      'Click "Update Payment" in the toolbar',
      'Record payment amount and details, then save'
    ],
    web: { videoUrl: 'videos/quotes/update-quote-payment.webm' }
  },
  {
    id: 'view_customer_from_quote',
    text: 'View Customer from Quote',
    tooltip: 'Navigate to customer details from a selected quotation',
    icon: 'business',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/quotes',
    requiresContext: true,
    keywords: ['view customer', 'customer details', 'quote customer', 'lead details'],
    steps: [
      'Navigate to Quotations page',
      'Select ONE quotation',
      'Click "View Customer" button',
      'Customer/Lead details will open'
    ]
  },
  // === REPORTS & ANALYTICS ===
  {
    id: 'view_deal_report',
    text: 'View Deal Report',
    tooltip: 'View comprehensive deal performance and analytics report',
    icon: 'analytics',
    category: 'user',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    route: '/opportunities/opportunity-table',
    keywords: ['deal report', 'deal analytics', 'sales report', 'opportunity report', 'deal performance'],
    steps: [
      'Navigate to Deals page',
      'Ensure no deals are selected',
      'Click "Deal Report" button',
      'Report will be displayed with analytics and charts'
    ]
  },
  // === ASSIGN LEADS FROM TASKS ===
  {
    id: 'assign_leads_from_tasks',
    text: 'Assign Leads from Tasks',
    tooltip: 'Assign the underlying leads associated with selected tasks',
    icon: 'assignment_ind',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    route: '/todo/grid',
    requiresContext: true,
    keywords: ['assign leads', 'assign from tasks', 'task leads', 'reassign task leads'],
    steps: [
      'Navigate to Tasks page',
      'Select one or more tasks using checkboxes',
      'Click "Assign Leads" button (assigns underlying contacts)',
      'Select user or team',
      'Click "Assign"'
    ]
  },
  // === PAYMENT OPERATIONS ===
  {
    id: 'create_payment_link',
    text: 'Create Payment Link',
    tooltip: 'Generate a Razorpay/Cashfree payment link or record an offline payment from a lead\'s detail page. Open a lead → click "Request Payment" → choose provider → enter amount → submit',
    icon: 'link',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: MANAGER_PLUS,
    route: '/opportunities/payments/table',
    keywords: ['payment link', 'invoice link', 'collect payment', 'share link', 'razorpay', 'cashfree', 'online payment', 'request payment', 'generate link'],
    videoUrl: 'videos/payments/create-payment-link.webm',
    steps: [
      'Navigate to Leads and click on a lead name to open detail sidebar',
      'Click "Request Payment" in the action buttons',
      'Choose payment provider — Razorpay, Cashfree, or Record Offline',
      'Enter the payment amount and select currency',
      'Click Submit — the payment link is generated and auto-copied to clipboard'
    ],
    web: { videoUrl: 'videos/payments/create-payment-link.webm' }
  },
  {
    id: 'record_offline_payment',
    text: 'Record Offline Payment',
    tooltip: 'Log a payment received offline (cash, bank transfer, cheque) — open a lead, click ACTION > Request Payment, select "Record Offline", enter amount and status',
    icon: 'attach_money',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/payments/table',
    keywords: ['offline payment', 'cash payment', 'bank transfer', 'record payment', 'cheque', 'manual payment', 'payment received'],
    videoUrl: 'videos/payments/create-payment-link.webm',
    steps: [
      'Navigate to Leads and click on a lead name to open detail sidebar',
      'Click ACTION button, then "Request Payment" in the dropdown',
      'Select "Record Offline" as the payment provider',
      'Enter amount, select payment status (Created/Paid/Partial Paid), add notes',
      'Click Submit to record the offline payment'
    ],
    web: { videoUrl: 'videos/payments/create-payment-link.webm' }
  },
  {
    id: 'record_partial_payment',
    text: 'Record Partial Payment',
    tooltip: 'Record a partial payment against an existing offline payment request from a lead\'s detail page. Open ACTION → View Payments, click Update on the offline payment, select "Record Partial Payment", enter the amount collected, and save.',
    icon: 'payments',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: MANAGER_PLUS,
    route: '/opportunities/payments/table',
    keywords: ['partial payment', 'part payment', 'split payment', 'installment', 'part paid', 'partial paid', 'offline payment update', 'amount collected', 'remaining balance'],
    videoUrl: 'videos/payments/record-partial-payment.webm',
    steps: [
      'Open the Lead Detail page for a lead with an existing offline payment',
      'Click ACTION → View Payments to open Payment History',
      'Find the offline payment and click "Update"',
      'Select "Record Partial Payment" from the two options',
      'Enter the partial amount received',
      'Optionally add a note about this payment',
      'Click "Update" to save — status changes to "Partial Paid"'
    ],
    web: {
      videoUrl: 'videos/payments/record-partial-payment.webm',
    }
  },
  {
    id: 'mark_as_paid',
    text: 'Mark as Paid',
    tooltip: 'Mark a pending offline payment as fully paid from the lead detail page. Open the lead, find the open payment under Important, click Update, select "Mark As Paid", and save.',
    icon: 'task_alt',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: MANAGER_PLUS,
    route: '/opportunities/quotes',
    keywords: ['mark paid', 'payment received', 'quote paid', 'close payment', 'full payment', 'paid in full', 'offline paid', 'complete payment'],
    videoUrl: 'videos/payments/record-partial-payment.webm',
    steps: [
      'Open the Lead Detail page for a lead with a pending offline payment',
      'Find the open payment under the Important tab',
      'Click "Update" on the payment card',
      'Select "Mark As Paid" from the two options',
      'Optionally add a note',
      'Click "Update" to save — status changes to "Paid"'
    ],
    web: {
      videoUrl: 'videos/payments/record-partial-payment.webm',
    }
  },
  {
    id: 'update_payment_status',
    text: 'Update Payment Status',
    tooltip: 'Change the status of an existing offline payment from the lead detail page. Find the open payment under Important, click Update, then choose "Mark As Paid" or "Record Partial Payment".',
    icon: 'update',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: MANAGER_PLUS,
    route: '/opportunities/payments/table',
    keywords: ['update payment', 'change payment status', 'modify payment', 'payment update', 'edit payment', 'payment status change'],
    videoUrl: 'videos/payments/record-partial-payment.webm',
    steps: [
      'Open the Lead Detail page for a lead with a pending offline payment',
      'Find the open payment under the Important tab',
      'Click "Update" on the payment card',
      'Choose "Mark As Paid" (full) or "Record Partial Payment" (installment)',
      'Enter amount if partial, optionally add a note',
      'Click "Update" to save'
    ],
    web: {
      videoUrl: 'videos/payments/record-partial-payment.webm',
    }
  },
  {
    id: 'view_open_payments',
    text: 'View Open Payments',
    tooltip: 'View all pending/open payment requests across leads on the Payments table page. See summary totals for Pending, Paid, and Cancelled, filter by status, select rows to update or export.',
    icon: 'receipt_long',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/payments/table',
    keywords: ['open payments', 'unpaid invoice', 'pending payment', 'payment table', 'all payments', 'payment requests', 'payment list', 'pending amount'],
    videoUrl: 'videos/payments/view-open-payments.webm',
    steps: [
      'Navigate to Opportunities → Payments (table view)',
      'View summary stats — Pending Payments, Paid Already, Request Cancelled',
      'Click "Pending Payments" card to filter to open/unpaid requests',
      'Browse the AG Grid table with lead name, amount, provider, status',
      'Select a row to see toolbar actions — Update, History, Cancel, Export'
    ],
    web: {
      videoUrl: 'videos/payments/view-open-payments.webm',
    }
  },
  {
    id: 'view_payment_history',
    text: 'View Payment History',
    tooltip: 'View the full payment history for a lead from the lead detail page. Click ACTION → View Payments to see all payment requests, statuses, amounts, dates, and action links in a sidebar.',
    icon: 'history',
    category: 'user',
    featureCategory: 'Deals & Quotations',
    allowedRoles: ALL_ROLES,
    route: '/opportunities/payments/table',
    keywords: ['payment history', 'transaction history', 'view payments', 'payment list', 'payment requests', 'payment status', 'all payments'],
    videoUrl: 'videos/payments/view-payment-history.webm',
    steps: [
      'Open the Lead Detail page for a lead with payments',
      'Click the ACTION button to open the action menu',
      'Click "View Payments" under the Activity column',
      'Payment History sidebar opens showing all payment requests',
      'Each card shows provider, amount, status, dates, and action links',
      'Use "Cancel Request" or "Update" to manage individual payments'
    ],
    web: {
      videoUrl: 'videos/payments/view-payment-history.webm',
    }
  },
  // === LEAD INLINE ACTIONS ===
  {
    id: 'assign_lead_single',
    text: 'Assign Lead (Single)',
    tooltip: 'Assign or reassign the currently selected lead',
    icon: 'person_add',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    workflowId: 'assign-single-lead',
    keywords: ['assign lead', 'reassign lead', 'change owner', 'single lead'],
    steps: [
      'Select the lead from the table or detail page',
      'Click "Assign" button',
      'Choose user, team, or distribution rule',
      'Confirm to assign'
    ]
  },
  {
    id: 'set_follow_up_on_lead',
    text: 'Set Follow-up on Lead',
    tooltip: 'Schedule a follow-up task/reminder for a lead',
    icon: 'alarm',
    category: 'user',
    featureCategory: 'Tasks & Follow-ups',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    keywords: ['follow up', 'schedule task', 'reminder', 'set follow up'],
    steps: [
      'Open lead detail or select lead in list',
      'Click "Create Task/Follow-up"',
      'Choose due date/time and assignee',
      'Save the follow-up task'
    ]
  },
  {
    id: 'edit_multiple_lead',
    text: 'Edit Multiple Leads',
    tooltip: 'Bulk edit fields for selected leads',
    icon: 'edit_note',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    route: '/leads/all-leads',
    requiresContext: true,
    keywords: ['bulk edit leads', 'edit multiple leads', 'mass update'],
    steps: [
      'Select one or more leads via checkboxes',
      'Click "Edit" or pencil icon in toolbar',
      'Update the fields in the sidebar',
      'Click "Save" to apply updates'
    ]
  },
  {
    id: 'set_up_video_call',
    text: 'Set up Video Call',
    tooltip: 'Schedule or start a video call with leads/customers',
    icon: 'videocam',
    category: 'user',
    featureCategory: 'Communication & Calling',
    allowedRoles: ALL_ROLES,
    component: undefined,
    route: '/engagements/video-calls',
    keywords: ['video call', 'google meet', 'video meeting', 'set up call'],
    steps: [
      'Open lead detail or call toolkit',
      'Click "Video Call" button',
      'Choose participants',
      'Send invite or copy join link'
    ]
  },
  {
    id: 'view_single_lead_history',
    text: 'View Lead History',
    tooltip: 'View all activities and changes for a lead',
    icon: 'timeline',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    keywords: ['lead history', 'activity timeline', 'view lead log'],
    steps: [
      'Open lead detail panel',
      'Scroll to history/timeline section',
      'Review activities and changes'
    ]
  },
  {
    id: 'view_all_vs_my_data',
    text: 'View All vs My Leads',
    tooltip: 'Toggle between My data and All data',
    icon: 'visibility',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    keywords: ['my leads', 'all leads', 'team leads', 'view switch'],
    steps: [
      'Open Leads page',
      'Use the "My" / "All" toggle above the table',
      'Select the desired scope'
    ]
  },
  {
    id: 'delete_smart_view',
    text: 'Delete Smart View',
    tooltip: 'Delete a saved smart view',
    icon: 'delete',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    route: '/leads/all-leads',
    keywords: ['delete smart view', 'remove saved view'],
    steps: [
      'Switch to the smart view you want to delete',
      'Open the smart view menu (three dots)',
      'Click "Delete" and confirm'
    ]
  },
  {
    id: 'add_smart_view_on_app',
    text: 'Create Smart View',
    tooltip: 'Save current filters as a smart view',
    icon: 'bookmark_add',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    keywords: ['save view', 'smart view', 'custom view'],
    steps: [
      'Apply filters on the grid',
      'Click "Save View" or star icon',
      'Name the view and choose visibility',
      'Save it for future use'
    ]
  },
  {
    id: 'move_active_lead_from_new_to_prospecting',
    text: 'Move Lead to Prospecting',
    tooltip: 'Change lifecycle from New to Prospecting',
    icon: 'swap_horiz',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    keywords: ['move lead', 'prospecting', 'change lifecycle stage'],
    steps: [
      'Select the lead(s)',
      'Open "Change Lifecycle" dialog',
      'Choose Prospecting',
      'Save the change'
    ]
  },
  {
    id: 'close_active_lead_already_customer',
    text: 'Close Lead (Already Customer)',
    tooltip: 'Mark lead as closed because they are already a customer',
    icon: 'thumb_up_alt',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    requiresContext: true,
    keywords: ['close lead', 'already customer', 'mark closed'],
    steps: [
      'Select the lead(s)',
      'Click "Close Lead" or use detail panel',
      'Choose reason "Already a customer"',
      'Confirm closing'
    ]
  },
  {
    id: 'reopen_closed_leads',
    text: 'Reopen Closed Leads',
    tooltip: 'Rechurn previously closed leads',
    icon: 'history_toggle_off',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    route: '/leads/closed-leads',
    requiresContext: true,
    keywords: ['reopen lead', 'rechurn', 'activate closed lead'],
    steps: [
      'Navigate to Closed Leads view',
      'Select leads',
      'Click "Reopen/Rechurn"',
      'Confirm action'
    ]
  },
  {
    id: 'auto_close_leads_dnt',
    text: 'Auto Close (Not Interested)',
    tooltip: 'Automatically close leads marked as not interested',
    icon: 'rule',
    category: 'admin',
    featureCategory: 'Campaigns & Automation',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:do-not-track)',
    keywords: ['auto close leads', 'not interested automation', 'auto close'],
    steps: [
      'Navigate to Admin > Settings > Automations',
      'Find "Auto Close Not Interested leads"',
      'Enable toggle and choose criteria',
      'Save automation'
    ]
  },
  {
    id: 'auto_close_leads_not_picking',
    text: 'Auto Close (Not Picking)',
    tooltip: 'Automatically close leads who are not picking calls',
    icon: 'phone_missed',
    category: 'admin',
    featureCategory: 'Campaigns & Automation',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:do-not-track)',
    keywords: ['auto close', 'not picking', 'automation'],
    steps: [
      'Navigate to Admin > Settings > Automations',
      'Locate "Auto Close Not Picking leads"',
      'Set threshold (# attempts, duration)',
      'Enable and save'
    ]
  },
  {
    id: 'reorder_columns_in_table',
    text: 'Reorder Table Columns',
    tooltip: 'Open column customization panel to reorder/hide columns',
    icon: 'view_column',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    keywords: ['reorder columns', 'customize table', 'column settings'],
    steps: [
      'On Leads page, click column icon (top-right of table)',
      'Drag and drop columns in the list to reorder',
      'Toggle visibility using eye icons',
      'Changes reflect immediately'
    ]
  },
  {
    id: 'create_custom_chart_or_table',
    text: 'Create Custom Chart/Table',
    tooltip: 'Build custom reports using Chart Gallery',
    icon: 'insights',
    category: 'user',
    featureCategory: 'Reports & Analytics',
    allowedRoles: MANAGER_PLUS,
    component: ChartGalleryComponent,
    dialogType: DialogType.FULL_MODAL,
    closeOnClickOutside: true,
    keywords: ['custom chart', 'custom table', 'report builder', 'analytics']
  },
  {
    id: 'delete_single_lead',
    text: 'Delete Single Lead',
    tooltip: 'Delete the current lead from detail view',
    icon: 'delete',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    route: '/leads/all-leads',
    requiresContext: true,
    keywords: ['delete lead', 'remove lead', 'delete contact', 'lead delete kaise kare', 'lead hatao', 'bulk delete'],
    videoUrl: 'videos/leads/delete-single-lead.webm',
    web: { videoUrl: 'videos/leads/delete-single-lead.webm' },
    steps: [
      'Go to Leads > Active Leads',
      'Select one or more leads (use filters to narrow down)',
      'Click the Delete button in the toolbar',
      'Optionally check "Do Not Track" to block the number',
      'Click "Yes, delete it!" to confirm'
    ]
  },
  {
    id: 'delete_or_archive_property',
    text: 'Delete Custom Property',
    tooltip: 'Delete or archive a custom field/property',
    icon: 'category',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:properties)',
    keywords: ['delete property', 'archive field', 'custom field'],
    steps: [
      'Go to Admin > Settings > Properties',
      'Locate the custom field',
      'Click delete/archive icon',
      'Confirm removal'
    ]
  },
  {
    id: 'delete_call_recording',
    text: 'Delete Call Recording',
    tooltip: 'Remove a call recording entry (Super Admin only)',
    icon: 'voice_over_off',
    category: 'admin',
    featureCategory: 'Communication & Calling',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/engagements/calls',
    videoUrl: 'videos/calls/delete-call-recording.webm',
    web: { videoUrl: 'videos/calls/delete-call-recording.webm' },
    keywords: ['delete recording', 'remove call recording', 'call log delete'],
    steps: [
      'Navigate to Callings page',
      'Select one or more call records using checkboxes',
      'Click "Delete" button in toolbar (Super Admin only)',
      'Confirm deletion'
    ]
  },
  {
    id: 'delete_document',
    text: 'Delete Document/File',
    tooltip: 'Delete an uploaded document from CRM',
    icon: 'delete_forever',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: MANAGER_PLUS,
    route: '/leads/documents',
    keywords: ['delete document', 'remove file', 'delete attachment'],
    steps: [
      'Open Documents section on lead/deal',
      'Click delete icon next to file',
      'Confirm removal'
    ]
  },
  {
    id: 'view_absolute_date_time_format',
    text: 'Use Absolute Date-Time',
    tooltip: 'Switch date display to absolute format (e.g., 12 Dec 2025 10:30)',
    icon: 'calendar_today',
    category: 'user',
    featureCategory: 'Settings & Customization',
    allowedRoles: ALL_ROLES,
    route: '/account/profile/(profile:preference-reports)',
    keywords: ['absolute date', 'date format', 'show exact time'],
    steps: [
      'Navigate to Account > Preferences',
      'Find Date/Time display setting',
      'Select "Absolute" format',
      'Save preference'
    ]
  },
  {
    id: 'view_relative_date_time_format',
    text: 'Use Relative Date-Time',
    tooltip: 'Switch date display to relative format (e.g., 5 minutes ago)',
    icon: 'schedule',
    category: 'user',
    featureCategory: 'Settings & Customization',
    allowedRoles: ALL_ROLES,
    route: '/account/profile/(profile:preference-reports)',
    keywords: ['relative time', 'ago format', 'time display'],
    steps: [
      'Navigate to Account > Preferences',
      'Find Date/Time display setting',
      'Select "Relative" format',
      'Save preference'
    ]
  },
  // add_or_edit_deal_amount removed — merged into edit_deal (same kanban page, subfeature)
  // move_deal_stage_manual removed — duplicate of update_deal_stage
  {
    id: 'remove_lead_from_dnt',
    text: 'Remove Lead from Do Not Track',
    tooltip: 'Restore leads that were marked as Do Not Track — start tracking them again',
    icon: 'how_to_reg',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:do-not-track)',
    keywords: ['remove dnt', 'do not track list', 'restore lead', 'unblock number', 'track again', 'dnt list se hatao'],
    videoUrl: 'videos/admin/remove-lead-from-dnt.webm',
    web: { videoUrl: 'videos/admin/remove-lead-from-dnt.webm' },
    steps: [
      'Dashboard > CUSTOMIZE > "Manage Do Not Track Leads"',
      'Or go to Admin > Settings > Do Not Track',
      'Select the number you want to track again',
      'Click "Remove from Do Not Track"',
      'Confirm — the number can now be saved as a lead again'
    ]
  },
  // === WORKSPACE / DEVICE ===
  {
    id: 'get_workspace_pin',
    text: 'Get Workspace PIN/Device Key',
    tooltip: 'Fetch the device PIN/Key for mobile login or kiosk',
    icon: 'pin',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/account/manage-account',
    keywords: ['workspace pin', 'device key', 'mobile otp', 'login pin'],
    steps: [
      'Go to Account > Manage Workspace',
      'Find Device/Pin section',
      'Generate/View PIN',
      'Share with user'
    ]
  },
  {
    id: 'log_out',
    text: 'Log Out Now',
    tooltip: 'Sign out of the current Salesmax session',
    icon: 'logout',
    category: 'common',
    featureCategory: 'Settings & Customization',
    allowedRoles: ALL_ROLES,
    route: '/account/manage-account',
    keywords: ['logout', 'sign out', 'log off'],
    steps: [
      'Open profile/avatar menu',
      'Click "Logout"',
      'Confirm to sign out'
    ]
  },
  {
    id: 'import_phone_contacts',
    text: 'Import Phone Contacts',
    tooltip: 'Import contacts from phone into Salesmax mobile app',
    icon: 'import_contacts',
    category: 'user',
    featureCategory: 'Lead & Contact Management',
    allowedRoles: ALL_ROLES,
    route: '/leads/all-leads',
    keywords: ['import phone contacts', 'phonebook import', 'mobile contacts'],
    steps: [
      'Open Salesmax mobile app',
      'Go to Settings > Import Contacts',
      'Grant contact permissions',
      'Select contacts to import',
      'Confirm import'
    ]
  },
  // === ATTENDANCE ===
  {
    id: 'mark_attendance',
    text: 'Mark Attendance',
    tooltip: 'Mark start of day attendance',
    icon: 'task_alt',
    category: 'user',
    featureCategory: 'Team & Users',
    allowedRoles: ALL_ROLES,
    route: '/metrics/attendance',
    keywords: ['mark attendance', 'attendance check-in', 'start day'],
    steps: [
      'Open Attendance page or mobile widget',
      'Click "Check In"',
      'Confirm location/photo if required',
      'Submit'
    ]
  },
  {
    id: 'mark_checkout',
    text: 'Mark End of Day Checkout',
    tooltip: 'Mark end of day checkout/checkout time',
    icon: 'logout',
    category: 'user',
    featureCategory: 'Team & Users',
    allowedRoles: ALL_ROLES,
    route: '/metrics/attendance',
    keywords: ['mark checkout', 'end of day', 'checkout'],
    steps: [
      'At end of day, open Attendance screen',
      'Click "Check Out"',
      'Confirm submission'
    ]
  },
  {
    id: 'mark_leave',
    text: 'Apply Leave',
    tooltip: 'Mark a day as leave/off',
    icon: 'event_busy',
    category: 'user',
    featureCategory: 'Team & Users',
    allowedRoles: ALL_ROLES,
    route: '/metrics/attendance',
    keywords: ['mark leave', 'apply leave', 'leave request'],
    steps: [
      'Open Attendance/Leave section',
      'Choose Leave type and date',
      'Provide reason if required',
      'Submit request'
    ]
  },
  // === PROFILE / SECURITY ===
  {
    id: 'change_login_mobile_number',
    text: 'Change Login Mobile Number',
    tooltip: 'Update the registered login mobile number',
    icon: 'phone_iphone',
    category: 'user',
    featureCategory: 'Settings & Customization',
    allowedRoles: ALL_ROLES,
    route: '/account/profile/(profile:profile-detail)',
    keywords: ['change mobile number', 'update login number', 'phone change'],
    steps: [
      'Go to Account > Profile',
      'Click "Edit Contact Details"',
      'Update mobile number',
      'Verify OTP if prompted',
      'Save changes'
    ]
  },
  // === TUTORIALS & HOW-TOS ===
  {
    id: 'full_tutorial_app_admin_manual_deal_mode',
    text: 'Full Tutorial (App Admin - Manual)',
    tooltip: 'Watch the complete app tutorial for admins using manual deal stage updates',
    icon: 'smartphone',
    category: 'admin',
    featureCategory: 'Help & Tutorials',
    allowedRoles: ADMIN_ONLY,
    keywords: ['tutorial', 'mobile tutorial', 'admin tutorial', 'manual deal mode'],
    steps: [
      'Open the Help/Tutorials section in the app',
      'Select "Admin Manual Deal Mode" tutorial',
      'Watch the step-by-step walkthrough'
    ]
  },
  {
    id: 'full_tutorial_app_admin_auto_deal_mode',
    text: 'Full Tutorial (App Admin - Auto)',
    tooltip: 'Watch the complete app tutorial for admins using auto deal mode',
    icon: 'smartphone',
    category: 'admin',
    featureCategory: 'Help & Tutorials',
    allowedRoles: ADMIN_ONLY,
    keywords: ['tutorial', 'mobile tutorial', 'admin tutorial', 'auto deal mode']
  },
  {
    id: 'full_tutorial_app_non_admin_manual_deal_mode',
    text: 'Full Tutorial (App User - Manual)',
    tooltip: 'Walkthrough video for non-admin users (manual deal mode) on the mobile app',
    icon: 'smartphone',
    category: 'user',
    featureCategory: 'Help & Tutorials',
    allowedRoles: ALL_ROLES,
    keywords: ['tutorial', 'mobile tutorial', 'manual mode']
  },
  {
    id: 'full_tutorial_app_non_admin_auto_deal_mode',
    text: 'Full Tutorial (App User - Auto)',
    tooltip: 'Walkthrough video for non-admin users using auto deal mode on the mobile app',
    icon: 'smartphone',
    category: 'user',
    featureCategory: 'Help & Tutorials',
    allowedRoles: ALL_ROLES,
    keywords: ['tutorial', 'mobile tutorial', 'auto mode']
  },
  {
    id: 'full_tutorial_web_admin_manual_deal_mode',
    text: 'Full Tutorial (Web Admin - Manual)',
    tooltip: 'Complete web tutorial for admins operating manual deal stages',
    icon: 'desktop_windows',
    category: 'admin',
    featureCategory: 'Help & Tutorials',
    allowedRoles: ADMIN_ONLY,
    keywords: ['tutorial', 'web tutorial', 'admin manual deal']
  },
  {
    id: 'full_tutorial_web_admin_auto_deal_mode',
    text: 'Full Tutorial (Web Admin - Auto)',
    tooltip: 'Complete web tutorial for admins using auto deal mode',
    icon: 'desktop_windows',
    category: 'admin',
    featureCategory: 'Help & Tutorials',
    allowedRoles: ADMIN_ONLY,
    keywords: ['tutorial', 'web tutorial', 'admin auto deal']
  },
  {
    id: 'full_tutorial_web_non_admin_manual_deal_mode',
    text: 'Full Tutorial (Web User - Manual)',
    tooltip: 'Web tutorial for team members working in manual deal mode',
    icon: 'desktop_windows',
    category: 'user',
    featureCategory: 'Help & Tutorials',
    allowedRoles: ALL_ROLES,
    keywords: ['tutorial', 'web tutorial', 'manual deal']
  },
  {
    id: 'full_tutorial_web_non_admin_auto_deal_mode',
    text: 'Full Tutorial (Web User - Auto)',
    tooltip: 'Web tutorial for team members in auto deal mode',
    icon: 'desktop_windows',
    category: 'user',
    featureCategory: 'Help & Tutorials',
    allowedRoles: ALL_ROLES,
    keywords: ['tutorial', 'web tutorial', 'auto deal']
  },
  {
    id: 'how_to_install_app',
    text: 'Install Salesmax App',
    tooltip: 'Instructions to install the Salesmax mobile application',
    icon: 'download',
    category: 'user',
    featureCategory: 'Help & Tutorials',
    allowedRoles: ALL_ROLES,
    keywords: ['install app', 'download app', 'mobile install'],
    steps: [
      'Open Google Play Store / Apple App Store',
      'Search for "Salesmax CRM"',
      'Tap Install and log in with workspace credentials'
    ]
  },
  {
    id: 'manage_app_permissions',
    text: 'Manage App Preferences & Permissions',
    tooltip: 'Configure mobile app permissions (location, contacts, notifications)',
    icon: 'settings_applications',
    category: 'admin',
    featureCategory: 'Settings & Customization',
    allowedRoles: SUPERADMIN_ONLY,
    route: '/admin/settings/(settings:role-access)',
    keywords: ['app permissions', 'mobile permissions', 'manage app preferences'],
    steps: [
      'Open Admin > Settings > Role-Based Access',
      'Review mobile permissions (location, contact sync, notifications)',
      'Enable or disable toggles per role',
      'Save changes'
    ]
  }
];
